/**
 * Quiz Player Component
 * Allows students to take quizzes
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { FiClock, FiCheckCircle, FiArrowLeft, FiArrowRight, FiBookOpen, FiUser, FiCalendar, FiFileText, FiChevronDown, FiChevronUp, FiAlertCircle, FiRefreshCw, FiInfo, FiAward, FiX } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import apiService from '../../services/api';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import QuizSaveManager from '../../utils/quizSaveManager';
import tokenRefreshManager from '../../utils/tokenRefreshManager';
import QuizReAuthModal from '../../components/QuizReAuthModal';
import useRealTimeCountdown from '../../hooks/useRealTimeCountdown';
import logger from '../../utils/logger';
import { useToast } from '../../components/common/ToastProvider';
import styles from './QuizPlayer.module.css';

/* eslint-disable no-unused-vars */

const getApiErrorMessage = (error, fallbackMessage) => {
  return error?.data?.message || error?.response?.data?.message || error?.message || fallbackMessage;
};

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  /* Prevent text selection and copying */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  
  /* Prevent drag and drop */
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  
  /* Prevent image dragging */
  img {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
  }
  
  /* Ensure proper Arabic text rendering */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Arabic', sans-serif;
  direction: ltr;
  
  /* Arabic text specific styling */
  * {
    unicode-bidi: embed;
  }
  
  /* Ensure Arabic text displays properly */
  [dir="rtl"], .arabic-text {
    direction: rtl;
    text-align: right;
    unicode-bidi: bidi-override;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
  
  /* Mobile viewport optimizations */
  @media (max-width: 768px) {
    /* Prevent horizontal scroll on mobile */
    overflow-x: hidden;
    /* Ensure proper touch scrolling */
    -webkit-overflow-scrolling: touch;
    /* Prevent zoom on input focus (iOS) */
    font-size: 16px;
    /* Safe area support for notched devices (iPhone X+ through iPhone 17 Pro Max) */
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
  }
  
  /* Large iPhone models (iPhone 15 Pro Max, iPhone 16 Pro Max, iPhone 17 Pro Max - 430px) */
  @media (max-width: 430px) {
    padding-left: max(0.875rem, env(safe-area-inset-left));
    padding-right: max(0.875rem, env(safe-area-inset-right));
  }
  
  @media (max-width: 480px) {
    /* Safe area support for smaller devices */
    padding-left: max(0.75rem, env(safe-area-inset-left));
    padding-right: max(0.75rem, env(safe-area-inset-right));
  }
`;

// eslint-disable-next-line no-unused-vars
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    margin-bottom: 1.75rem;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1.25rem;
    gap: 0.75rem;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    padding: 0.625rem;
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const QuizTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.25rem;
  }
`;

const QuizDescription = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.875rem;
  }
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: ${props => {
    if (props.warning) return 'rgba(246, 224, 94, 0.1)';
    if (props.danger) return 'rgba(252, 129, 129, 0.1)';
    return 'var(--background-secondary)';
  }};
  color: ${props => {
    if (props.warning) return 'var(--warning-dark)';
    if (props.danger) return 'var(--error-color)';
    return 'var(--text-primary)';
  }};
  border: 2px solid ${props => {
    if (props.warning) return 'var(--warning-color)';
    if (props.danger) return 'var(--error-color)';
    return 'var(--border-color)';
  }};
  border-radius: 0;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    justify-content: center;
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
    /* Better text rendering on mobile */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--background-tertiary);
  border-radius: 0;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const QuestionCard = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SaveStatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 0;
  background: ${props => {
    if (props.status === 'saving') return 'rgba(246, 224, 94, 0.1)';
    if (props.status === 'saved') return 'rgba(104, 211, 145, 0.1)';
    if (props.status === 'failed') return 'rgba(252, 129, 129, 0.1)';
    return 'var(--background-secondary)';
  }};
  color: ${props => {
    if (props.status === 'saving') return 'var(--warning-dark)';
    if (props.status === 'saved') return 'var(--success-dark)';
    if (props.status === 'failed') return 'var(--error-color)';
    return 'var(--text-muted)';
  }};
  
  svg {
    flex-shrink: 0;
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background: var(--error-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--error-dark);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const NetworkStatusIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'offline') return 'rgba(252, 129, 129, 0.1)';
    if (props.status === 'unstable') return 'rgba(246, 224, 94, 0.1)';
    return 'rgba(104, 211, 145, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'offline') return 'var(--error-color)';
    if (props.status === 'unstable') return 'var(--warning-dark)';
    return 'var(--success-dark)';
  }};
  border: 1px solid ${props => {
    if (props.status === 'offline') return 'var(--error-color)';
    if (props.status === 'unstable') return 'var(--warning-color)';
    return 'var(--success-color)';
  }};
  
  svg {
    flex-shrink: 0;
  }
`;

const NetworkErrorBanner = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${props => {
    if (props.severity === 'critical') return 'rgba(252, 129, 129, 0.1)';
    if (props.severity === 'warning') return 'rgba(246, 224, 94, 0.1)';
    if (props.severity === 'info') return props.style?.backgroundColor || 'rgba(96, 165, 250, 0.1)';
    return 'rgba(96, 165, 250, 0.1)';
  }};
  color: ${props => {
    if (props.severity === 'critical') return 'var(--error-color)';
    if (props.severity === 'warning') return 'var(--warning-dark)';
    if (props.severity === 'info') return props.style?.color || 'var(--info-color)';
    return 'var(--info-color)';
  }};
  border-bottom: 2px solid ${props => {
    if (props.severity === 'critical') return 'var(--error-color)';
    if (props.severity === 'warning') return 'var(--warning-color)';
    if (props.severity === 'info') return props.style?.borderColor || 'var(--info-color)';
    return 'var(--info-color)';
  }};
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9375rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .banner-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    
    svg {
      flex-shrink: 0;
    }
  }
  
  .banner-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .dismiss-button {
    background: transparent;
    border: 1px solid currentColor;
    color: currentColor;
    padding: 0.375rem 0.75rem;
    border-radius: 0;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.2s;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
`;

// UI FIX: Auto-submit overlay component
const AutoSubmitOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  
  .spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .message {
    color: var(--text-inverse);
    font-size: 1.25rem;
    font-weight: bold;
    text-align: center;
  }
  
  .submessage {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    text-align: center;
  }
`;

// UI FIX: Timer expired text with pulse animation
const TimerExpiredText = styled.span`
  color: var(--error-color);
  font-weight: bold;
  animation: pulse 1s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const QuestionNumber = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const QuestionPoints = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-inverse);
  background: var(--contrast-accent);
  padding: 0.375rem 0.875rem;
  border-radius: 0;
  border: 1px solid var(--contrast-accent);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ExtraCreditBadge = styled.span`
  display: inline-block;
  margin-left: 0.75rem;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, var(--warning-color), var(--warning-dark));
  color: var(--text-inverse);
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
  
  @media (max-width: 768px) {
    margin-left: 0.5rem;
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    margin-left: 0.25rem;
    padding: 0.15rem 0.3rem;
    font-size: 0.65rem;
  }
`;

const QuestionPrompt = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-primary);
  min-height: 44px; /* Ensure minimum touch target size */
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: var(--border-dark);
    background: var(--background-secondary);
  }

  &.selected {
    border-color: var(--primary-color);
    background: rgba(246, 224, 94, 0.1);
  }

  &:focus,
  &:focus-visible,
  &:focus-within,
  &.selected:focus,
  &.selected:focus-visible,
  &.selected:focus-within {
    outline: none;
    box-shadow: none;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 0.875rem;
    gap: 0.625rem;
    min-height: 48px; /* Larger touch target on mobile */
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
    min-height: 44px;
  }
`;

const OptionInput = styled.input`
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  accent-color: var(--primary-color);
  cursor: pointer;
  flex-shrink: 0;
  
  /* Mobile optimizations - ensure touch target is large enough */
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
  }
  
  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
  }
`;

const OptionText = styled.span`
  flex: 1;
  font-size: 1rem;
  color: var(--text-primary);
`;

const OptionInstruction = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
  font-style: italic;
  padding: 0.5rem;
  background: var(--background-secondary);
  border-radius: 0;
  border-left: 3px solid var(--primary-color);
`;

const FillBlankInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: var(--background-primary);
  box-sizing: border-box;
  
  /* Allow text selection in input fields */
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 16px; /* Prevent iOS zoom on focus */
    min-height: 44px; /* Ensure minimum touch target */
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 16px; /* Ensure 16px to prevent iOS zoom */
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 100%;
  overflow-x: auto;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    order: 2;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
  
  @media (max-width: 1024px) {
    order: 2;
    width: 100%;
    margin: 0.5rem 0;
  }
`;

const PaginationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 2px solid ${props => {
    if (props.$active) return 'var(--primary-color)';
    if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.2)';
    if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.2)';
    return 'var(--border-color)';
  }};
  background: ${props => {
    if (props.$active) return 'var(--primary-color)';
    if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.1)';
    if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.1)';
    return 'var(--background-primary)';
  }};
  color: ${props => {
    if (props.$active) return 'var(--text-inverse)';
    if (props.$visited && props.$answered) return 'var(--success-dark)';
    if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
    return 'var(--text-secondary)';
  }};
  border-radius: 0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
  
  &:hover {
    border-color: ${props => {
      if (props.$active) return 'var(--primary-dark)';
      if (props.$visited && props.$answered) return 'var(--success-dark)';
      if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
      return 'var(--primary-color)';
    }};
    background: ${props => {
      if (props.$active) return 'var(--primary-dark)';
      if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.15)';
      if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.15)';
      return 'rgba(246, 224, 94, 0.1)';
    }};
    color: ${props => {
      if (props.$active) return 'var(--text-inverse)';
      if (props.$visited && props.$answered) return 'var(--success-dark)';
      if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
      return 'var(--text-secondary)';
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
    /* Ensure minimum touch target size for mobile */
    min-width: 44px;
    min-height: 44px;
  }
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 1024px) {
    order: 1;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    order: 1;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
    /* Ensure minimum touch target size for mobile */
    min-height: 44px;
    /* Better touch feedback */
    -webkit-tap-highlight-color: rgba(216, 184, 137, 0.2);
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
    }
  }

  &.success {
    background: var(--success-color);
    color: var(--text-inverse);
    border-color: var(--success-color);

    &:hover:not(:disabled) {
      background: var(--success-dark);
    }
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }

  &.saved {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--error-color);
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
  margin: 2rem 0;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--error-color);
  margin: 0 0 1rem 0;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const ErrorAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
`;

// Review Page Components
const ReviewContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ReviewHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
`;

const ReviewTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
`;


// Use transient prop ($unanswered) to prevent passing to DOM
const ReviewQuestion = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: ${props => props.$unanswered ? 'rgba(252, 129, 129, 0.1)' : 'var(--background-primary)'};
  border-radius: 0;
  border-left: 4px solid ${props => props.$unanswered ? 'var(--error-color)' : 'var(--primary-color)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

const ReviewQuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;


const ReviewQuestionPoints = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  background: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ReviewQuestionPrompt = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewQuestionPromptWithHTML = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  /* Style for fill-in-the-blank answers */
  .fill-blank-answer {
    background: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
    padding: 0.25rem 0.5rem;
    border-radius: 0;
    border: 1px solid var(--primary-color);
    font-weight: 600;
    margin: 0 0.25rem;
  }
`;

const ReviewAnswer = styled.div`
  margin-top: 1rem;
`;

const ReviewAnswerText = styled.div`
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--background-secondary);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--border-color);
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewAnswerEmpty = styled.div`
  font-size: 1rem;
  color: var(--error-color);
  font-style: italic;
  background: rgba(252, 129, 129, 0.1);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--error-color);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
`;

const ReviewOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ReviewOption = styled.div`
  padding: 0.75rem;
  border-radius: 0;
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.selected ? 'rgba(246, 224, 94, 0.1)' : 'var(--background-primary)'};
  color: ${props => props.selected ? 'var(--warning-dark)' : 'var(--text-primary)'};
  font-weight: ${props => props.selected ? '600' : '500'};
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: all 0.2s ease;
  
  ${props => props.selected && `
    &::before {
      content: '✓';
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--primary-color);
      font-weight: bold;
      font-size: 1.1rem;
    }
  `}
  
  &:hover {
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--border-dark)'};
    background: ${props => props.selected ? 'rgba(246, 224, 94, 0.1)' : 'var(--background-secondary)'};
  }
`;

const ReviewActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.625rem;
  }
`;

const ReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border: 2px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  flex: 1;
  min-height: 44px;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      border-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
  }

  &.success {
    background: var(--success-color);
    color: var(--text-inverse);
    border-color: var(--success-color);

    &:hover:not(:disabled) {
      background: var(--success-dark);
      border-color: var(--success-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    min-height: 48px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const EditLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  border: 2px solid var(--contrast-accent);
  background: var(--contrast-accent);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: auto;
  height: auto;

  &:hover {
    background: var(--background-primary);
    border-color: var(--contrast-accent);
    color: var(--contrast-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(26, 55, 93, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.75rem;
    font-size: 0.8rem;
    min-width: auto;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem 0.6rem;
    font-size: 0.75rem;
  }
`;



const QuizDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin: 1rem auto 0 auto;
  padding: 1rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border-radius: 0;
  border: 1px solid rgba(216, 184, 137, 0.1);
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 800px;
  width: 100%;
  box-sizing: border-box;
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    max-width: 90%;
    margin: 1rem auto 0 auto;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin: 0.75rem auto 0 auto;
    padding: 0.875rem;
    font-size: 0.875rem;
    max-width: 95%;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8125rem;
    gap: 0.5rem;
    margin: 0.5rem auto 0 auto;
    max-width: 98%;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    padding: 0.625rem;
    font-size: 0.75rem;
    gap: 0.5rem;
    margin: 0.375rem auto 0 auto;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 400;
  color: var(--text-secondary);
  padding: 0.25rem 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
  
  span {
    line-height: 1.4;
    word-break: break-word;
    flex: 1;
  }
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    gap: 0.5rem;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    justify-content: flex-start;
    text-align: left;
    gap: 0.5rem;
    padding: 0.375rem 0;
    align-items: center;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    gap: 0.5rem;
    font-size: 0.8125rem;
    padding: 0.375rem 0;
    flex-direction: row;
    align-items: center;
    text-align: left;
    justify-content: flex-start;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    gap: 0.5rem;
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }
`;

const CustomDropdown = styled.div`
  position: relative;
  flex: 1;
  z-index: 1;
  
  &:focus-within {
    z-index: 10000;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  background: var(--background-primary);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  min-height: 48px;
  text-align: left;
  display: flex;
  align-items: center;
  color: ${props => props.disabled ? 'var(--text-muted)' : (props.hasValue ? 'var(--text-primary)' : 'var(--text-muted)')};
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  @media (max-width: 1024px) {
    padding: 0.6875rem;
    padding-right: 2.375rem;
    font-size: 0.9375rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem;
    padding-right: 2.25rem;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation; // Better touch handling
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-right: 2rem;
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 360px) {
    padding: 0.4375rem;
    padding-right: 1.875rem;
    font-size: 0.8125rem;
    min-height: 38px;
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    right: 0.625rem;
  }
  
  @media (max-width: 480px) {
    right: 0.5rem;
  }
`;

const DropdownMenu = styled.div.attrs(props => ({
  style: props.position ? {
    position: 'fixed',
    top: `${props.position.top}px`,
    left: `${props.position.left}px`,
    width: `${props.position.width}px`,
    maxWidth: 'calc(100vw - 2rem)',
  } : {}
}))`
  margin-top: 0.25rem;
  background: var(--background-primary);
  border: 2px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: visible;
  
  @media (max-width: 1024px) {
    max-height: 280px;
  }
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 1rem) !important;
    max-height: 250px;
    font-size: 0.9rem;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    max-width: calc(100vw - 0.5rem) !important;
    max-height: 200px;
    font-size: 0.85rem;
    border-radius: 0;
  }
  
  @media (max-width: 360px) {
    max-height: 180px;
    font-size: 0.8125rem;
  }
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: var(--background-primary);
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-primary);
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-secondary);
  }

  &.selected {
    background: rgba(246, 224, 94, 0.1);
    color: var(--text-primary);
  }
  
  @media (max-width: 1024px) {
    padding: 0.6875rem 0.9375rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation; // Better touch handling
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 360px) {
    padding: 0.4375rem 0.625rem;
    font-size: 0.8125rem;
    min-height: 38px;
  }
`;

const MatchingHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }
  
  h4 {
    @media (max-width: 768px) {
      font-size: 0.9375rem !important;
      margin-bottom: 0.75rem !important;
    }
    
    @media (max-width: 480px) {
      font-size: 0.875rem !important;
      margin-bottom: 0.5rem !important;
    }
  }
`;

const MatchingItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  align-items: center;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
    padding: 0.9375rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0.875rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 0;
  }
  
  @media (max-width: 360px) {
    padding: 0.625rem;
    gap: 0.625rem;
  }
  
  > div:first-child {
    @media (max-width: 768px) {
      margin-bottom: 0.5rem;
      font-size: 0.9375rem;
      line-height: 1.5;
    }
    
    @media (max-width: 480px) {
      font-size: 0.875rem;
      margin-bottom: 0.375rem;
    }
    
    @media (max-width: 360px) {
      font-size: 0.8125rem;
    }
  }
`;

const getInstructorName = (assignment) => {
  if (assignment?.instructor_first_name && assignment?.instructor_last_name) {
    return `${assignment.instructor_first_name} ${assignment.instructor_last_name}`;
  } else if (assignment?.instructor_first_name) {
    return assignment.instructor_first_name;
  } else if (assignment?.instructor_last_name) {
    return assignment.instructor_last_name;
  }
  return 'Unknown Instructor';
};

function QuizPlayer() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const isRetakeMode = searchParams.get('retake') === 'true';
  const isEditMode = searchParams.get('edit') === 'true';

  const [attempt, setAttempt] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  
  // Refs for latest state (fixes stale closure issues)
  const responsesRef = useRef({});
  const setResponsesRef = useRef(null);
  
  // Save manager instance
  const saveManagerRef = useRef(null);
  
  // Debounce timers for immediate auto-save
  const debounceTimersRef = useRef({});

  // Questions with local changes that still need persistence.
  const dirtyQuestionIdsRef = useRef(new Set());
  const initialSavedResponsesRef = useRef({});
  
  // Timeout timers for clearing "saved" status after 5 seconds
  const savedStatusTimeoutsRef = useRef({});
  
  // Track manual saves to extend status timeout
  const manualSaveFlagsRef = useRef({});
  
  // FIX 1: Track component mount status to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // FIX 2: Track periodic save timeout to prevent multiple timeouts
  const periodicSaveTimeoutRef = useRef(null);
  
  // FIX 3: Track timed quiz interval to prevent multiple intervals
  const timedSaveIntervalRef = useRef(null);
  
  // Track if save manager has been initialized with existing responses
  const hasInitializedSaveManagerRef = useRef(false);
  
  // Track last reset time to prevent rapid flickering from multiple resets
  const lastIndexResetRef = useRef(0);
  
  // Save status tracking (per question) - used by save manager callback
  // eslint-disable-next-line no-unused-vars
  const [saveStatuses, setSaveStatuses] = useState({}); // questionId -> { status, timestamp, error }
  
  // Check if any answers are currently being saved (for submit button disable)
  const hasUnsavedAnswers = useMemo(() => {
    // CRITICAL FIX: When on the last question, only check the current question
    // Previous questions can continue saving in the background without blocking review mode
    if (quiz && currentQuestionIndex === quiz.questions.length - 1) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      if (currentQuestion) {
        const currentStatus = saveStatuses[currentQuestion.id];
        const hasCurrentSaving = currentStatus?.status === 'saving';
        const hasCurrentTimer = debounceTimersRef.current[currentQuestion.id] !== undefined;
        const hasCurrentActiveSave = saveManagerRef.current?.hasActiveSave(currentQuestion.id) || false;
        
        return hasCurrentSaving || hasCurrentTimer || hasCurrentActiveSave;
      }
    }
    
    // For other questions, check all questions (original behavior)
    // Check save statuses for 'saving' status
    const hasSavingStatus = Object.values(saveStatuses).some(
      status => status?.status === 'saving'
    );
    
    // Check pending debounce timers
    const hasPendingTimers = Object.keys(debounceTimersRef.current).length > 0;
    
    // Check save manager active saves
    const hasActiveSaves = saveManagerRef.current?.hasActiveSaves() || false;
    
    return hasSavingStatus || hasPendingTimers || hasActiveSaves;
  }, [saveStatuses, quiz, currentQuestionIndex]);
  
  // Count unsaved answers for status message
  const unsavedCount = useMemo(() => {
    // CRITICAL FIX: When on the last question, only count the current question
    if (quiz && currentQuestionIndex === quiz.questions.length - 1) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      if (currentQuestion) {
        const currentStatus = saveStatuses[currentQuestion.id];
        const currentSaving = currentStatus?.status === 'saving' ? 1 : 0;
        const currentTimer = debounceTimersRef.current[currentQuestion.id] !== undefined ? 1 : 0;
        return Math.max(currentSaving, currentTimer);
      }
      return 0;
    }
    
    // For other questions, count all (original behavior)
    const savingCount = Object.values(saveStatuses).filter(
      status => status?.status === 'saving'
    ).length;
    
    const pendingTimerCount = Object.keys(debounceTimersRef.current).length;
    
    // Return the maximum count (most accurate)
    return Math.max(savingCount, pendingTimerCount);
  }, [saveStatuses, quiz, currentQuestionIndex]);

  // Helper function to validate if a response has a valid answer based on question type
  // CRITICAL: Only returns true if an answer is actually selected/entered (not empty)
  // FIX #7: Added explicit empty string checks and improved validation
  const hasValidAnswer = useCallback((questionType, response) => {
    if (!response) return false;
    
    // FIX #7: Helper to check if a value is a valid non-empty ID
    const isValidId = (id) => {
      if (id === null || id === undefined) return false;
      if (typeof id === 'number') return !isNaN(id);
      if (typeof id === 'string') return id.trim() !== '';
      return false;
    };
    
    if (questionType === 'multiple_choice') {
      // Check if any options are selected (support both new and old format)
      if (response.selected_option_ids && Array.isArray(response.selected_option_ids) && response.selected_option_ids.length > 0) {
        // FIX #7: Validate that at least one ID is valid
        return response.selected_option_ids.some(id => isValidId(id));
      }
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // FIX #7: Validate that at least one ID is valid
            return parsed.some(id => isValidId(id));
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      // Legacy single selection - must have a valid selected_option_id
      // FIX #7: Use isValidId helper for consistent validation
      return isValidId(response.selected_option_id);
    } else if (questionType === 'true_false') {
      // Must have a selected option (valid ID)
      // FIX #7: Use isValidId helper for consistent validation
      return isValidId(response.selected_option_id);
    } else if (questionType === 'matching') {
      // Check if matching pairs exist in answer_text (must have at least one pair)
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            // FIX #7 & #12: Validate that pairs have valid structure and non-negative indices
            return pairs.some(pair =>
              pair &&
              typeof pair === 'object' &&
              typeof pair.leftIndex === 'number' &&
              typeof pair.rightIndex === 'number' &&
              pair.leftIndex >= 0 &&
              pair.rightIndex >= 0
            );
          }
        } catch (e) {
          // Not valid JSON
        }
      }
      return false;
    } else if (questionType === 'fill_blank') {
      // For fill_blank, check if answer_text has non-empty content
      if (response.answer_texts && Array.isArray(response.answer_texts)) {
        // Multiple blanks - at least one must have non-empty content
        return response.answer_texts.some(answer => answer && typeof answer === 'string' && answer.trim() !== '');
      }
      // Single blank - check answer_text has non-empty content
      // FIX #7: Ensure answer_text is not just whitespace
      return response.answer_text &&
             typeof response.answer_text === 'string' &&
             response.answer_text.trim() !== '';
    }
    
    return false;
  }, []);

  const getMatchingCompletionStatus = useCallback((question, response) => {
    if (!question || question.type !== 'matching') {
      return { answeredCount: 0, totalCount: 0, isPartiallyAnswered: false, isComplete: true };
    }

    const leftItems = question.settings?.leftItems || question.leftItems || [];
    const totalCount = Array.isArray(leftItems)
      ? leftItems.filter(item => item && item.trim()).length
      : 0;

    let answeredCount = 0;
    if (response?.answer_text) {
      try {
        const pairs = JSON.parse(response.answer_text);
        if (Array.isArray(pairs)) {
          const answeredLeftIndices = new Set(
            pairs
              .filter(pair =>
                pair &&
                typeof pair === 'object' &&
                typeof pair.leftIndex === 'number' &&
                typeof pair.rightIndex === 'number' &&
                pair.leftIndex >= 0 &&
                pair.rightIndex >= 0
              )
              .map(pair => pair.leftIndex)
          );
          answeredCount = answeredLeftIndices.size;
        }
      } catch (err) {
        answeredCount = 0;
      }
    }

    return {
      answeredCount,
      totalCount,
      isPartiallyAnswered: answeredCount > 0 && answeredCount < totalCount,
      isComplete: totalCount === 0 || answeredCount >= totalCount
    };
  }, []);
  
  // Constants
  const AUTO_SUBMIT_SAVE_TIMEOUT_MS = 3000; // Maximum time to wait for saves before auto-submitting
  const AUTO_SUBMIT_MODAL_SECONDS = 10; // Countdown shown for timed single-attempt auto-submit
  
  // Ref to avoid dependency issues with handleSubmit
  const handleSubmitRef = useRef();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timerDeadlineMs, setTimerDeadlineMs] = useState(null); // Absolute deadline for useRealTimeCountdown
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false); // Track if user came from review to edit
  const [error, setError] = useState(null);
  const [firewallWarning, setFirewallWarning] = useState(null);
  const [skipConfirmation, setSkipConfirmation] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = useRef({});
  const questionHeaderRef = useRef(null);
  const questionCardRef = useRef(null);
  const comingFromReviewRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [permissions, setPermissions] = useState(null); // Permissions are validated on backend; stored here for potential future UI enhancements

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.keys(openDropdowns).some(key => openDropdowns[key])) {
        const target = event.target;
        if (!target.closest('[data-dropdown]')) {
          setOpenDropdowns({});
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);

  useEffect(() => {
    if (!skipConfirmation) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSkipConfirmation(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [skipConfirmation]);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  
  // Re-authentication modal state (for untimed quizzes when token expires)
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [reAuthSavedCount, setReAuthSavedCount] = useState(0);
  
  // Network connectivity state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkStatus, setNetworkStatus] = useState('online'); // 'online' | 'offline' | 'unstable'
  const [networkErrorBanner, setNetworkErrorBanner] = useState(null); // { type, message, count }
  const [queuedAnswersCount, setQueuedAnswersCount] = useState(0);
  const connectivityCheckIntervalRef = useRef(null);
  const connectivityCheckInFlightRef = useRef(false);
  const isOnlineRef = useRef(navigator.onLine);
  const networkStatusRef = useRef('online');
  const queuedAnswersCountRef = useRef(0);
  const saveStatusesRef = useRef({});
  const responsesSnapshotRef = useRef({});

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    networkStatusRef.current = networkStatus;
  }, [networkStatus]);

  useEffect(() => {
    queuedAnswersCountRef.current = queuedAnswersCount;
  }, [queuedAnswersCount]);

  useEffect(() => {
    saveStatusesRef.current = saveStatuses;
  }, [saveStatuses]);

  useEffect(() => {
    responsesSnapshotRef.current = responses;
  }, [responses]);
  
  // CRITICAL FIX: Cache CSRF token for unload handlers (can't await async in beforeunload)
  const csrfTokenRef = useRef(null);
  const csrfTokenRefreshIntervalRef = useRef(null);
  // FIX: Store CSRF visibility change handler ref for cleanup
  const csrfVisibilityHandlerRef = useRef(null);


  const formatFillBlankPrompt = (prompt) => {
    if (!prompt) return '';
    // Replace single underscores with 5 underscores for better visual indication
    return prompt.replace(/_{1,}/g, '_____');
  };

  const formatQuestionTypeLabel = (type) => {
    const labels = {
      multiple_choice: 'MULTIPLE CHOICE',
      true_false: 'TRUE/FALSE',
      fill_blank: 'FILL IN THE BLANK',
      matching: 'MATCHING'
    };

    return labels[type] || String(type || '').replace(/_/g, ' ').toUpperCase();
  };

  const stripQuestionPromptPrefix = (prompt) => {
    if (!prompt) return '';
    return prompt.replace(/^(?:Multiple Choice|True\/False|Fill in the blank|Fill in the Blank|Matching)\s+Q\d+:\s*/i, '');
  };

  // Handle Arabic text encoding properly
  const handleArabicText = (text) => {
    if (!text) return '';
    
    // If text contains question marks, it might be corrupted Arabic text
    if (text.includes('???') || text.includes('؟؟؟')) {
      logger.warn('Detected potentially corrupted Arabic text', { text: text.substring(0, 100) });
      // Try to decode if it's URL encoded
      try {
        const decoded = decodeURIComponent(encodeURIComponent(text));
        if (decoded !== text) {
          return decoded;
        }
      } catch (e) {
        logger.warn('Failed to decode Arabic text', { error: e });
      }
    }
    
    // Ensure proper Unicode handling for Arabic text
    try {
      // This ensures proper UTF-8 handling
      return decodeURIComponent(encodeURIComponent(text));
    } catch (e) {
      logger.warn('Failed to process Arabic text', { error: e });
      return text;
    }
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return '';
    const date = new Date(dueDate);
    // Format as: "Month Day, Year at Time"
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  // Check refresh token availability before starting quiz
  useEffect(() => {
    if (attemptId && !tokenRefreshManager.hasRefreshToken()) {
      logger.warn('Refresh token not available - user may need to re-authenticate');
      setError('Your session is not properly authenticated. Please refresh the page or log in again.');
    } else if (attemptId && tokenRefreshManager.isRefreshTokenExpiringSoon()) {
      logger.warn('Refresh token expiring soon');
      // Show warning but don't block quiz
      const timeUntilExpiry = tokenRefreshManager.getTimeUntilRefreshTokenExpires();
      const hoursRemaining = Math.floor(timeUntilExpiry / (60 * 60 * 1000));
      if (hoursRemaining <= 1) {
        toast.warning(`Your session will expire in less than ${hoursRemaining} hour(s). Please save your answers frequently.`, { duration: 9000 });
      }
    }
  }, [attemptId, toast]);

  const fetchAttempt = useCallback(async () => {
    try {
      setLoading(true);
      const response = await quizApiService.getAttempt(attemptId);
      
      if (response.success) {
        // Set attempt with assignment data included
        setAttempt({
          ...response.data.attempt,
          assignment: response.data.assignment
        });
        setQuiz(response.data.quiz);
        
        // FIX: Check if attempt is already submitted - redirect to results immediately
        // EXCEPT when opening in retake/edit mode via granted permissions.
        if (response.data.attempt.submitted_at && !isRetakeMode && !isEditMode) {
          logger.info('Attempt already submitted, redirecting to results...', { attemptId });
          // Navigate to results page immediately
          if (response.data.assignment?.immediate_feedback) {
            navigate(`/student/result/${attemptId}`);
          } else {
            navigate('/student/quizzes', {
              state: { message: 'This quiz has already been submitted. Results will be available after the due date.' }
            });
          }
          return;
        }
        
        // CRITICAL FIX: Validate and reset currentQuestionIndex if out of bounds
        // This handles cases where questions array is filtered (e.g., partial retake/edit)
        // Must be done AFTER setQuiz to ensure quiz state is updated
        if (response.data.quiz?.questions) {
          const questionsLength = response.data.quiz.questions.length;
          // Use a function to get the current state value
          setCurrentQuestionIndex(prevIndex => {
            if (prevIndex >= questionsLength) {
              logger.warn('currentQuestionIndex out of bounds on quiz load - likely due to partial retake/edit filtering', { 
                prevIndex, 
                questionsLength,
                attemptId,
                isRetakeMode,
                isEditMode,
                originalQuestionsCount: response.data.quiz?.questions?.length || 'unknown',
                filteredQuestionsCount: questionsLength,
                scenario: 'Student was on question #' + (prevIndex + 1) + ' but quiz was filtered to only ' + questionsLength + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set.'
              });
              return 0;
            }
            logger.debug('Quiz loaded successfully', { 
              questionsCount: questionsLength,
              currentQuestionIndex: prevIndex,
              attemptId
            });
            return prevIndex;
          });
        }
        
        // Debug: Log the full response data structure (temporarily for debugging)
        // console.log('[Quiz Load] Full response data:', JSON.stringify(response.data, null, 2));
        // console.log('[Quiz Load] Assignment data:', JSON.stringify(response.data.assignment, null, 2));
        // console.log('[Quiz Load] Quiz data:', JSON.stringify(response.data.quiz, null, 2));
        // console.log('[Quiz Load] Attempt data:', JSON.stringify(response.data.attempt, null, 2));
        
        // Initialize responses from existing data
        logger.debug('Loading existing responses', { count: response.data.existing_responses?.length || 0 });
        const existingResponses = {};
        
        // FIX #6: Define escape/unescape functions for pipe character handling
        const PIPE_ESCAPE = '\u001F'; // Unit separator character (rarely used in text)
        const unescapePipe = (text) => {
          if (!text) return text;
          return text.replace(new RegExp(PIPE_ESCAPE, 'g'), '|');
        };
        
        response.data.existing_responses.forEach(resp => {
          logger.debug('Loading response for question', {
            questionId: resp.question_id,
            selected_option_id: resp.selected_option_id,
            answer_text: resp.answer_text ? resp.answer_text.substring(0, 50) : null,
            has_answer_text: !!resp.answer_text
          });
          
          // FIX #6: Improved fill-in-the-blank parsing
          // Check if it's a JSON array first (new format for fill-in-blank with multiple answers)
          if (resp.answer_text && resp.answer_text.startsWith('[') && !resp.answer_text.startsWith('[[')) {
            try {
              const parsed = JSON.parse(resp.answer_text);
              if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string' || typeof item === 'number')) {
                // This is a fill-in-blank with JSON array format
                resp.answer_texts = parsed.map(item => String(item));
                // Don't continue to pipe parsing
                existingResponses[resp.question_id] = resp;
                return;
              }
            } catch (e) {
              // Not valid JSON array, continue to other parsing
            }
          }
          
          // Parse pipe-separated answers for fill-in-the-blank questions
          // FIX #6: Use escape sequence for literal pipes in user input
          if (resp.answer_text && resp.answer_text.includes('|') && !resp.answer_text.startsWith('[')) {
            // Split by pipe, then unescape any escaped pipes in individual answers
            resp.answer_texts = resp.answer_text.split('|').map(unescapePipe);
          } else if (resp.answer_text && !resp.answer_text.startsWith('[')) {
            resp.answer_texts = [unescapePipe(resp.answer_text)];
          }
          
          // Parse JSON array for multiple choice questions with multiple selections
          if (resp.answer_text && resp.answer_text.startsWith('[')) {
            try {
              const parsed = JSON.parse(resp.answer_text);
              if (Array.isArray(parsed)) {
                resp.selected_option_ids = parsed;
              }
            } catch (e) {
              // Not valid JSON, ignore
            }
          } else if (resp.selected_option_id && !resp.answer_text) {
            // Convert old format (only selected_option_id) to new format (answer_text as JSON array)
            // This ensures backward compatibility
            resp.answer_text = JSON.stringify([resp.selected_option_id]);
            resp.selected_option_ids = [resp.selected_option_id];
          }
          
          existingResponses[resp.question_id] = resp;
        });
        logger.debug('Setting responses state', { count: Object.keys(existingResponses).length });
        initialSavedResponsesRef.current = existingResponses;
        setResponses(existingResponses);

        // Get time limit from assignment only (no quiz default time limits)
        const timeLimitSeconds = response.data.assignment?.time_limit_minutes 
          ? response.data.assignment.time_limit_minutes * 60 
          : null;
        
        const isPermissionEditSession =
          (isRetakeMode || isEditMode) && !!response.data.attempt?.submitted_at;

        const assignmentDueDate = response.data.assignment?.due_at || response.data.assignment?.assignment_due_at || response.data.quiz?.due_at;
        setDueDate(assignmentDueDate || null);

        if (timeLimitSeconds && !isPermissionEditSession) {
          // Get server time to sync timer and account for clock skew
          let serverTime = null;
          try {
            const serverTimeResponse = await quizApiService.getServerTime();
            serverTime = serverTimeResponse;
          } catch (e) {
            logger.warn('Failed to get server time, using client time', { error: e });
          }
          
          const remaining = await quizApiService.calculateTimeRemaining(
            response.data.attempt.started_at,
            timeLimitSeconds,
            serverTime
          );
          
          // Calculate absolute deadline for real-time countdown
          // Use server time if available, otherwise use client time
          const startedAt = new Date(response.data.attempt.started_at);
          const now = serverTime ? new Date(serverTime) : new Date();
          const elapsed = Math.floor((now - startedAt) / 1000); // seconds elapsed
          const remainingMs = Math.max(0, (timeLimitSeconds - elapsed) * 1000);
          // Use now.getTime() instead of Date.now() to ensure consistency with server time
          const deadlineMs = now.getTime() + remainingMs;
          
          logger.debug('Quiz timer initialized', { 
            startedAt: response.data.attempt.started_at,
            timeLimitSeconds,
            elapsed,
            remaining,
            deadlineMs,
            deadlineDate: new Date(deadlineMs).toISOString(),
            usingServerTime: !!serverTime
          });
          
          // Set both the deadline (for useRealTimeCountdown) and initial time remaining (for backwards compatibility)
          setTimerDeadlineMs(deadlineMs);
          setTimeRemaining(remaining);
          
          // FIX: Tell save manager this is a timed quiz
          if (saveManagerRef.current) {
            saveManagerRef.current.setIsTimedQuiz(true);
          }
        } else {
          if (isPermissionEditSession) {
            logger.info('Disabling timer for permission-based edit/retake session', {
              attemptId,
              isRetakeMode,
              isEditMode,
              submittedAt: response.data.attempt?.submitted_at,
              timeLimitSeconds
            });
          }

          setTimerDeadlineMs(null);
          setTimeRemaining(null);
          // Enable auto-save for untimed quizzes
          setAutoSaveEnabled(true);
          
          // FIX: Tell save manager this is an untimed quiz (enables re-auth modal on token expiry)
          if (saveManagerRef.current) {
            saveManagerRef.current.setIsTimedQuiz(false);
          }

        }
      } else {
        setError(response.message || 'Failed to load quiz attempt');
      }
    } catch (err) {
      logger.error('Error fetching attempt', { error: err, attemptId });
      
      // Check if the error is due to attempt already being submitted
      const backendMessage = getApiErrorMessage(err, 'Failed to load quiz attempt');
      if (backendMessage && backendMessage.includes('Attempt already submitted')) {
        setIsAutoSubmitted(true);
        setError('This quiz was automatically submitted when you refreshed the page. Please check your quiz results.');
        // Navigate to results page after a short delay
        setTimeout(() => {
          navigate(`/student/result/${attemptId}`);
        }, 3000);
      } else {
        setError(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [attemptId, navigate, isRetakeMode, isEditMode]);

  const fetchPermissions = useCallback(async () => {
    if (isRetakeMode || isEditMode) {
      try {
        const response = await quizApiService.getAttemptPermissions(attemptId);
        if (response.success && response.data) {
          // Permissions are validated on backend; stored here for potential future UI enhancements
          setPermissions(response.data);
          logger.debug('Retake/Edit permissions loaded', {
            allowRetakeAll: response.data.allowRetakeAll,
            allowEditAll: response.data.allowEditAll,
            retakeQuestionIds: response.data.retakeQuestionIds?.length || 0,
            editQuestionIds: response.data.editQuestionIds?.length || 0
          });
        }
      } catch (err) {
        logger.error('Error fetching permissions', { error: err, attemptId });
      }
    }
  }, [attemptId, isRetakeMode, isEditMode]);

  // Load quiz attempt data on mount
  useEffect(() => {
    if (attemptId) {
      fetchAttempt();
    }
  }, [attemptId, fetchAttempt]);

  // Load permissions if in retake/edit mode
  useEffect(() => {
    if (attemptId && (isRetakeMode || isEditMode)) {
      fetchPermissions();
    }
  }, [attemptId, isRetakeMode, isEditMode, fetchPermissions]);

  // CRITICAL FIX: Validate currentQuestionIndex is within bounds when quiz loads or questions array changes
  // This handles cases where questions array is filtered (e.g., partial retake/edit)
  // This is a safety net in case the fix in fetchAttempt didn't catch it
  // NOTE: Only runs when quiz.questions.length changes, not on every currentQuestionIndex change to prevent flickering
  const questionsLength = quiz?.questions?.length;
  useEffect(() => {
    // Only run if we have questions and a valid length
    if (!quiz?.questions || questionsLength === undefined) {
      return;
    }
    
    // Use a functional update to get the current index value and only update if needed
    setCurrentQuestionIndex(prevIndex => {
      if (prevIndex >= questionsLength) {
        logger.warn('currentQuestionIndex out of bounds - safety net caught it (likely partial retake/edit filtering)', { 
          prevIndex, 
          questionsLength,
          attemptId,
          scenario: 'Student was on question #' + (prevIndex + 1) + ' but quiz was filtered to only ' + questionsLength + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set.'
        });
        return 0;
      }
      // Return same value - React will skip re-render if value hasn't changed
      return prevIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsLength, attemptId]); // Only depend on length, not the array itself to prevent infinite loops

  // FIX 1: Set mounted status on mount/unmount
  // FIX #2: Comprehensive cleanup of debounce timers on unmount
  useEffect(() => {
    isMountedRef.current = true;
    window.sessionStorage?.setItem('faithinsight_active_quiz_attempt', 'true');
    return () => {
      isMountedRef.current = false;
      window.sessionStorage?.removeItem('faithinsight_active_quiz_attempt');
      window.sessionStorage?.setItem('faithinsight_quiz_flow_last_active_at', String(Date.now()));
      
      // FIX #2: Clear all debounce timers on unmount to prevent memory leaks
      // and state updates after unmount
      Object.values(debounceTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      debounceTimersRef.current = {};
      
      // Clear all saved status timeouts
      Object.values(savedStatusTimeoutsRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      savedStatusTimeoutsRef.current = {};
      
      // Clear CSRF token refresh interval
      if (csrfTokenRefreshIntervalRef.current) {
        clearInterval(csrfTokenRefreshIntervalRef.current);
        csrfTokenRefreshIntervalRef.current = null;
      }
      
      // Cleanup save manager
      if (saveManagerRef.current) {
        saveManagerRef.current.cleanup();
      }
    };
  }, []);

  // Initialize save manager and offline support
  useEffect(() => {
    // Reset initialization flag when attempt changes (before creating new save manager)
    hasInitializedSaveManagerRef.current = false;
    
    if (attemptId) {
      const saveManager = new QuizSaveManager(attemptId, quizApiService);
      saveManagerRef.current = saveManager;
      saveManager.markResponsesSaved(initialSavedResponsesRef.current || {});
      
      // CRITICAL FIX: Set state refs immediately to avoid race condition
      // This ensures save manager has access to latest state from the start, preventing
      // auto-save failures when handleResponseChange is called before the separate useEffect
      // that updates refs (line 1705) runs
      saveManager.setStateRefs(responsesRef, setResponsesRef);
      let triggerConnectivityCheckForIssues = () => {};
      
      // RECOMMENDATION 2: Start periodic sync for failed saves queue
      saveManager.startPeriodicSync();
      
      // Note: Token refresh is now handled automatically by tokenRefreshManager
      // The setTokenRefreshCallback method is kept for backward compatibility but does nothing
      
      // Set status change callback
      saveManager.setStatusChangeCallback((questionId, status, error) => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) {
          return;
        }
        
        // Clear any existing timeout for this question
        if (savedStatusTimeoutsRef.current[questionId]) {
          clearTimeout(savedStatusTimeoutsRef.current[questionId]);
          delete savedStatusTimeoutsRef.current[questionId];
        }
        
        setSaveStatuses(prev => {
          const updated = {
            ...prev,
            [questionId]: {
              status,
              timestamp: Date.now(),
              error: error?.message || error
            }
          };
          
          // Update network error banner based on failed saves
          const failedCount = Object.values(updated).filter(s => s?.status === 'failed').length;
          const queuedCount = saveManager.failedSaveQueue?.length || 0;
          setQueuedAnswersCount(queuedCount);
          
          // Check if any failed saves are due to 503 Service Unavailable
          const hasServiceUnavailable = Object.values(updated).some(s => 
            s?.status === 'failed' && 
            (s?.error?.includes('503') || s?.error?.includes('Service temporarily unavailable'))
          );
          
          // Update banner if there are failed saves
          if (failedCount > 0 && networkStatusRef.current === 'online') {
            if (hasServiceUnavailable) {
              setNetworkErrorBanner({
                type: 'service_unavailable',
                severity: 'critical',
                message: `Service temporarily unavailable (503). ${failedCount} answer(s) failed to save. ${queuedCount > 0 ? `${queuedCount} queued for retry.` : ''}`,
                count: failedCount
              });
            } else {
              setNetworkErrorBanner({
                type: 'save_failed',
                severity: 'warning',
                message: `${failedCount} answer(s) failed to save. ${queuedCount > 0 ? `${queuedCount} queued for retry.` : ''}`,
                count: failedCount
              });
            }
          } else if (failedCount === 0 && queuedCount === 0 && networkStatusRef.current === 'online') {
            // Clear banner if all saves succeeded
            setNetworkErrorBanner(prev => {
              // Only clear if it's a save-related banner, not network status
              if (prev?.type === 'save_failed' || prev?.type === 'service_unavailable' || prev?.type === 'server_error') {
                return null;
              }
              return prev;
            });
          }
          
          return updated;
        });

        if (status === 'failed' || (saveManager.failedSaveQueue?.length || 0) > 0) {
          setTimeout(() => triggerConnectivityCheckForIssues(), 0);
        }
        
        // Clear "saved" status after timeout (PRIORITY 2 FIX: Extended timeout for manual saves)
        if (status === 'saved') {
          // Check if this was a manual save (extend timeout to 10 seconds)
          const isManualSave = manualSaveFlagsRef.current[questionId];
          const timeoutDuration = isManualSave ? 10000 : 5000; // 10s for manual, 5s for auto
          
          if (isManualSave) {
            delete manualSaveFlagsRef.current[questionId];
          }
          
          savedStatusTimeoutsRef.current[questionId] = setTimeout(() => {
            // FIX 1 & 4: Check if component is mounted before state update
            if (!isMountedRef.current) {
              delete savedStatusTimeoutsRef.current[questionId];
              return;
            }
            
            setSaveStatuses(prev => {
              const updated = { ...prev };
              // Only clear if status is still "saved" (not changed to something else)
              if (updated[questionId]?.status === 'saved') {
                delete updated[questionId];
              }
              return updated;
            });
            delete savedStatusTimeoutsRef.current[questionId];
          }, timeoutDuration);
        }
      });
      
      // Process any failed saves from previous session
      saveManager.processFailedQueue();
      
      // FIX: Set up re-authentication callback for untimed quizzes
      // This shows a re-auth modal instead of auto-submitting when token expires
      saveManager.setReAuthRequiredCallback(({ savedAnswersCount, error, attemptId: reAuthAttemptId }) => {
        // Only show modal if component is mounted and this is the current attempt
        if (!isMountedRef.current || reAuthAttemptId !== attemptId) {
          return;
        }
        
        logger.info('Re-authentication required for quiz', {
          attemptId,
          savedAnswersCount,
          error
        });
        
        setReAuthSavedCount(savedAnswersCount);
        setShowReAuthModal(true);
      });
      
      // Firewall detection: Listen for firewall detection events
      const handleFirewallDetected = () => {
        setFirewallWarning({
          message: 'Firewall detected. Requests may take longer than usual. Your answers are being saved automatically.',
          severity: 'info'
        });
      };
      
      const handleFirewallCleared = () => {
        setFirewallWarning(null);
      };
      
      const handleSlowNetwork = () => {
        setFirewallWarning({
          message: 'Slow network detected. Timeout has been increased to ensure your answers are saved.',
          severity: 'info'
        });
      };
      
      // Check initial state
      const checkInitialFirewallState = async () => {
        try {
          const apiService = (await import('../../services/api')).default;
          if (apiService.firewallDetected) {
            handleFirewallDetected({ detail: {} });
          } else if (apiService.slowNetworkDetected) {
            handleSlowNetwork();
          }
        } catch (err) {
          // Silently fail - not critical
        }
      };
      
      // Listen for firewall detection events
      window.addEventListener('firewall-detected', handleFirewallDetected);
      window.addEventListener('firewall-cleared', handleFirewallCleared);
      
      // Check initial state
      checkInitialFirewallState();
      
      // CRITICAL FIX: Pre-fetch and cache CSRF token for unload handlers
      // Unload handlers can't await async operations, so we cache the token synchronously
      let csrfApiService = null;
      
      // FIX: Helper function to refresh CSRF token (used by interval and visibility handler)
      const refreshCSRFToken = async () => {
        if (!csrfApiService) {
          csrfApiService = (await import('../../services/api')).default;
        }
        try {
          const token = await csrfApiService.fetchCSRFToken();
          if (token) {
            csrfTokenRef.current = token;
            logger.debug('CSRF token refreshed');
            return true;
          }
        } catch (err) {
          logger.warn('Failed to refresh CSRF token', { error: err });
        }
        return false;
      };
      
      // Initial CSRF token fetch
      (async () => {
        await refreshCSRFToken();
        logger.debug('CSRF token cached for unload handlers');
        
        // Periodically refresh CSRF token cache (every 30 minutes)
        csrfTokenRefreshIntervalRef.current = setInterval(async () => {
          await refreshCSRFToken();
        }, 30 * 60 * 1000); // 30 minutes
      })();
      
      // FIX: Refresh CSRF token when tab becomes visible (handles browser tab suspension)
      // This ensures CSRF token is fresh after laptop sleep, tab suspension, etc.
      // Throttle to prevent too many requests (max once per 30 seconds)
      let lastCSRFRefreshTime = 0;
      const CSRF_REFRESH_THROTTLE = 30000; // 30 seconds
      
      const handleCSRFVisibilityChange = async () => {
        if (document.visibilityState === 'visible') {
          const now = Date.now();
          // Only refresh if more than 30 seconds since last refresh
          if (now - lastCSRFRefreshTime >= CSRF_REFRESH_THROTTLE) {
            logger.debug('Tab became visible - refreshing CSRF token');
            lastCSRFRefreshTime = now;
            await refreshCSRFToken();
          } else {
            logger.debug('Tab became visible - CSRF refresh throttled');
          }
        }
      };
      
      // Store handler ref for cleanup
      csrfVisibilityHandlerRef.current = handleCSRFVisibilityChange;
      document.addEventListener('visibilitychange', handleCSRFVisibilityChange);
      
      // Basic offline support: cache quiz data in localStorage
      const cacheKey = `quiz_cache_${attemptId}`;
      const cacheQuizData = () => {
        try {
          const quizData = {
            quiz,
            attempt,
            responses: responsesSnapshotRef.current,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(quizData));
        } catch (e) {
          logger.warn('Failed to cache quiz data', { error: e });
        }
      };
      
      // Cache quiz data when it changes
      if (quiz && attempt) {
        cacheQuizData();
      }
      
      // Network connectivity detection and monitoring
      const checkConnectivity = async () => {
        if (connectivityCheckInFlightRef.current) {
          logger.debug('Connectivity check already in progress, skipping duplicate');
          return;
        }

        connectivityCheckInFlightRef.current = true;
        const wasOnline = isOnlineRef.current;
        const browserOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

        try {
          // Check actual connectivity by pinging server
          let serverReachable = false;
          let serverStatus = null; // Track specific server status (503, 500, etc.)
          if (browserOnline) {
            try {
              // IMPROVED: Increased timeout to 8 seconds for firewall environments
              // Try HEAD first (lightweight), fallback to GET if HEAD fails
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 8000); // Increased from 3s to 8s

              let response = null;
              let headFailed = false;

              // Try HEAD request first (lightweight)
              try {
                response = await fetch('/portal/api/config', {
                  method: 'HEAD',
                  signal: controller.signal,
                  cache: 'no-cache'
                });

                // Check if response is 404 - endpoint might not exist, try GET as fallback
                if (response.status === 404) {
                  headFailed = true;
                  clearTimeout(timeoutId);
                  const getController = new AbortController();
                  const getTimeoutId = setTimeout(() => getController.abort(), 8000);
                  try {
                    response = await fetch('/portal/api/config', {
                      method: 'GET',
                      signal: getController.signal,
                      cache: 'no-cache'
                    });
                    clearTimeout(getTimeoutId);
                  } catch (getErr) {
                    clearTimeout(getTimeoutId);
                    if (getErr.name !== 'AbortError') {
                      throw getErr;
                    }
                    response = null;
                  }
                }
              } catch (headErr) {
                // HEAD might be blocked by firewall or network error - try GET as fallback
                headFailed = true;
                // Only log if it's not an abort (timeout)
                if (headErr.name !== 'AbortError') {
                  logger.debug('HEAD request failed, trying GET fallback', { error: headErr });
                }

                // Clear existing timeout and create new one for GET
                clearTimeout(timeoutId);
                const getController = new AbortController();
                const getTimeoutId = setTimeout(() => getController.abort(), 8000);

                try {
                  response = await fetch('/portal/api/config', {
                    method: 'GET',
                    signal: getController.signal,
                    cache: 'no-cache'
                  });
                  clearTimeout(getTimeoutId);
                } catch (getErr) {
                  clearTimeout(getTimeoutId);
                  // Only throw if it's not an abort
                  if (getErr.name !== 'AbortError') {
                    throw getErr;
                  }
                  response = null;
                }
              }

              if (!headFailed) {
                clearTimeout(timeoutId);
              }

              // FIX #8: Improved response status handling to reduce false positives
              // Only check response status if we have a valid response
              if (response) {
                serverStatus = response.status;
                // Server is reachable if: OK (200), needs auth (401), or other non-5xx errors
                // FIX #8: 404 means endpoint doesn't exist - treat as REACHABLE (server responded)
                // This prevents false offline warnings when the config endpoint isn't implemented
                // 503 Service Unavailable means server is overloaded/down - treat as unreachable
                if (response.status === 404) {
                  // FIX #8: Endpoint doesn't exist but server responded - treat as reachable
                  // The server is working, just this specific endpoint isn't available
                  serverReachable = true;
                  serverStatus = 404; // Keep status for logging but don't treat as error
                  logger.debug('Config endpoint not found (404) but server is reachable');
                } else {
                  serverReachable = response.ok || response.status === 401 || (response.status < 500 && response.status !== 503);
                }
              } else {
                serverReachable = false;
                serverStatus = null;
              }
            } catch (err) {
              serverReachable = false;
              serverStatus = null;
              // Log connectivity check failure for debugging
              if (err.name !== 'AbortError') {
                logger.warn('Connectivity check failed', { error: err });
              }
            }
          }

          const queuedCount = queuedAnswersCountRef.current;
          const currentSaveStatuses = saveStatusesRef.current;
          const nowOnline = browserOnline && serverReachable;
          isOnlineRef.current = nowOnline;
          setIsOnline(nowOnline);

          // Determine network status
          if (!browserOnline) {
            networkStatusRef.current = 'offline';
            setNetworkStatus('offline');
            setNetworkErrorBanner({
              type: 'offline',
              severity: 'critical',
              message: 'You are offline. Answers will be saved when your connection returns.',
              count: queuedCount
            });
          } else if (!serverReachable) {
            // Only mark as unstable if we have a specific error status or failed/queued saves.
            if (serverStatus === 503) {
              networkStatusRef.current = 'unstable';
              setNetworkStatus('unstable');
              setNetworkErrorBanner({
                type: 'service_unavailable',
                severity: 'critical',
                message: 'Service temporarily unavailable (503). The server is overloaded or down. Your answers are being queued and will be saved when the service is available.',
                count: queuedCount
              });
            } else if (serverStatus === 500) {
              networkStatusRef.current = 'unstable';
              setNetworkStatus('unstable');
              setNetworkErrorBanner({
                type: 'server_error',
                severity: 'critical',
                message: 'Server error (500). Please try again in a moment. Your answers are being queued.',
                count: queuedCount
              });
            } else {
              const hasFailedSaves = Object.values(currentSaveStatuses).some(s => s?.status === 'failed');
              if (hasFailedSaves || queuedCount > 0) {
                networkStatusRef.current = 'unstable';
                setNetworkStatus('unstable');
                setNetworkErrorBanner({
                  type: 'unstable',
                  severity: 'warning',
                  message: 'Connection unstable. Some answers may not save properly.',
                  count: queuedCount
                });
              } else {
                logger.debug('Connectivity check failed but no failed saves, keeping status as online', {
                  serverStatus,
                  browserOnline
                });
              }
            }
          } else {
            networkStatusRef.current = 'online';
            setNetworkStatus('online');
            // Clear banner if we were offline/unstable and now online
            if (!wasOnline) {
              setNetworkErrorBanner({
                type: 'restored',
                severity: 'info',
                message: 'Connection restored. Saving queued answers...',
                count: 0
              });
              // Auto-dismiss success message after 5 seconds
              setTimeout(() => {
                setNetworkErrorBanner(null);
              }, 5000);
            } else {
              // Only clear if no other errors
              const hasFailedSaves = Object.values(currentSaveStatuses).some(s => s?.status === 'failed');
              if (!hasFailedSaves) {
                setNetworkErrorBanner(null);
              }
            }
          }
        } finally {
          connectivityCheckInFlightRef.current = false;
        }
      };

      const startIssueConnectivityPolling = () => {
        if (connectivityCheckIntervalRef.current) {
          return;
        }

        connectivityCheckIntervalRef.current = setInterval(() => {
          const hasFailedSaves = Object.values(saveStatusesRef.current).some(s => s?.status === 'failed');
          const hasQueuedSaves = queuedAnswersCountRef.current > 0 || (saveManager.failedSaveQueue?.length || 0) > 0;
          const needsPolling = hasFailedSaves || hasQueuedSaves || networkStatusRef.current !== 'online';

          if (!needsPolling) {
            clearInterval(connectivityCheckIntervalRef.current);
            connectivityCheckIntervalRef.current = null;
            return;
          }

          checkConnectivity();
        }, 30000);
      };

      triggerConnectivityCheckForIssues = () => {
        const hasFailedSaves = Object.values(saveStatusesRef.current).some(s => s?.status === 'failed');
        const hasQueuedSaves = queuedAnswersCountRef.current > 0 || (saveManager.failedSaveQueue?.length || 0) > 0;

        if (hasFailedSaves || hasQueuedSaves || networkStatusRef.current !== 'online') {
          checkConnectivity();
          startIssueConnectivityPolling();
        }
      };

      // Listen for online/offline events
      const handleOnline = () => {
        logger.debug('Network online event detected');
        checkConnectivity();
        saveManager.processFailedQueue();
      };
      
      const handleOffline = () => {
        logger.debug('Network offline event detected');
        setIsOnline(false);
        isOnlineRef.current = false;
        setNetworkStatus('offline');
        networkStatusRef.current = 'offline';
        setNetworkErrorBanner({
          type: 'offline',
          severity: 'critical',
          message: 'You are offline. Answers will be saved when your connection returns.',
          count: queuedAnswersCountRef.current
        });
      };

      const handleConnectivityVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          triggerConnectivityCheckForIssues();
        }
      };
      
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleConnectivityVisibilityChange);
      
      // Process any failed saves from previous session
      saveManager.processFailedQueue();
      triggerConnectivityCheckForIssues();
      
      // Cleanup function
      return () => {
        // Clean up debounce timers FIRST to prevent new saves during cleanup
        if (debounceTimersRef.current) {
          Object.values(debounceTimersRef.current).forEach(timer => {
            if (timer) clearTimeout(timer);
          });
          debounceTimersRef.current = {};
        }
        
        // Clean up saved status timeouts
        if (savedStatusTimeoutsRef.current) {
          Object.values(savedStatusTimeoutsRef.current).forEach(timer => {
            if (timer) clearTimeout(timer);
          });
          savedStatusTimeoutsRef.current = {};
        }
        
        // Clean up manual save flags
        manualSaveFlagsRef.current = {};
        
        // FIX 2: Clean up periodic save timeout
        if (periodicSaveTimeoutRef.current) {
          clearTimeout(periodicSaveTimeoutRef.current);
          periodicSaveTimeoutRef.current = null;
        }
        
        // FIX 3: Clean up timed quiz interval
        if (timedSaveIntervalRef.current) {
          clearInterval(timedSaveIntervalRef.current);
          timedSaveIntervalRef.current = null;
        }
        
        // Clean up connectivity check interval
        if (connectivityCheckIntervalRef.current) {
          clearInterval(connectivityCheckIntervalRef.current);
          connectivityCheckIntervalRef.current = null;
        }
        
        // Clean up CSRF token refresh interval
        if (csrfTokenRefreshIntervalRef.current) {
          clearInterval(csrfTokenRefreshIntervalRef.current);
          csrfTokenRefreshIntervalRef.current = null;
        }
        
        // FIX: Clean up CSRF visibility change handler
        if (csrfVisibilityHandlerRef.current) {
          document.removeEventListener('visibilitychange', csrfVisibilityHandlerRef.current);
          csrfVisibilityHandlerRef.current = null;
        }
        
        // Clean up firewall detection event listeners
        window.removeEventListener('firewall-detected', handleFirewallDetected);
        window.removeEventListener('firewall-cleared', handleFirewallCleared);
        
        // Then cleanup save manager
        if (saveManager) {
          saveManager.cleanup();
        }
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        document.removeEventListener('visibilitychange', handleConnectivityVisibilityChange);
      };
    }
  }, [attemptId, quiz, attempt]);
  
  // Update refs when responses change (fixes stale closure issues)
  // Note: State refs are also set during save manager initialization (line 1637),
  // but this ensures they stay updated as responses change
  useEffect(() => {
    responsesRef.current = responses;
    setResponsesRef.current = setResponses;
    
    // Update save manager refs (in case save manager was created before this effect ran)
    // This is a safety measure - refs are already set during initialization
    if (saveManagerRef.current) {
      saveManagerRef.current.setStateRefs(responsesRef, setResponsesRef);
    }
  }, [responses]);
  
  // Initialize save manager with existing responses when they're first loaded
  // This ensures questions loaded from database show as "saved" (green bordered)
  useEffect(() => {
    // Only initialize once when responses are first loaded and save manager exists
    const initialSavedResponses = initialSavedResponsesRef.current || {};
    if (!saveManagerRef.current || Object.keys(initialSavedResponses).length === 0) {
      return;
    }
    
    // Check if we've already initialized (to avoid re-initializing on every response change)
    if (hasInitializedSaveManagerRef.current) {
      return;
    }
    
    // Mark that we've initialized
    hasInitializedSaveManagerRef.current = true;
    
    logger.debug('Save Manager initializing', { responseCount: Object.keys(initialSavedResponses).length });
    Object.keys(initialSavedResponses).forEach(questionId => {
      const response = initialSavedResponses[questionId];
      if (response) {
        // Mark as saved in save manager's internal state
        saveManagerRef.current.markResponseSaved(questionId, response);
        
        // Set save status to "saved" (will show green border)
        // Note: Status will clear after 5 seconds (by design), but initial load shows "saved"
        if (isMountedRef.current) {
          setSaveStatuses(prev => ({
            ...prev,
            [questionId]: {
              status: 'saved',
              timestamp: Date.now(),
              error: null
            }
          }));
        }
      }
    });
    logger.debug('Save Manager initialized save status for existing responses');
  }, [responses]); // Only depends on responses - runs when responses are first loaded

  // No client-side auto-submit for untimed quizzes; nothing to clean up here

  const getDirtyValidResponses = useCallback(() => {
    const currentResponses = responsesRef.current || {};
    const dirtyIds = Array.from(dirtyQuestionIdsRef.current);
    const validResponses = {};

    if (!quiz?.questions || dirtyIds.length === 0) {
      return validResponses;
    }

    dirtyIds.forEach(qId => {
      const question = quiz.questions.find(q => String(q.id) === String(qId));
      const response = currentResponses[qId] || currentResponses[String(qId)];
      if (question && response && hasValidAnswer(question.type, response)) {
        validResponses[qId] = response;
      }
    });

    return validResponses;
  }, [quiz, hasValidAnswer]);

  // Auto-save for untimed quizzes using save manager (debounced, only saves changed questions)
  // FIX 2: Use ref to track timeout and prevent multiple timeouts
  useEffect(() => {
    if (!autoSaveEnabled || !saveManagerRef.current) {
      return;
    }
    
    // FIX 2: Clear existing timeout before creating new one
    if (periodicSaveTimeoutRef.current) {
      clearTimeout(periodicSaveTimeoutRef.current);
      periodicSaveTimeoutRef.current = null;
    }
    
    if (dirtyQuestionIdsRef.current.size === 0) {
      return;
    }
    
    periodicSaveTimeoutRef.current = setTimeout(async () => {
      // FIX 1: Check if component is mounted
      if (!isMountedRef.current) {
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      // Double-check conditions before saving
      if (!saveManagerRef.current) {
        logger.warn('Save manager not available during periodic save');
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      const validResponses = getDirtyValidResponses();
      
      if (Object.keys(validResponses).length === 0) {
        logger.debug('No valid responses to save in periodic auto-save');
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      logger.debug('Starting periodic auto-save', { 
        dirtyResponses: dirtyQuestionIdsRef.current.size,
        validResponses: Object.keys(validResponses).length 
      });
      
      try {
        const result = await saveManagerRef.current.saveAllResponses(validResponses, {
          skipUnchanged: true, // Only save changed questions
          skipVerification: true, // Skip verification for auto-save (faster)
          onProgress: (questionId, success) => {
            // FIX 1: Check if component is mounted before state update
            if (!isMountedRef.current) return;
            
            if (!success) {
              logger.warn('Failed to save question during auto-save', { questionId });
              // Update save status
              setSaveStatuses(prev => ({
                ...prev,
                [questionId]: {
                  status: 'failed',
                  timestamp: Date.now(),
                  error: 'Periodic save failed'
                }
              }));
            } else {
              dirtyQuestionIdsRef.current.delete(String(questionId));
            }
          }
        });
        
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) {
          periodicSaveTimeoutRef.current = null;
          return;
        }
        
        if (result.success) {
          Object.keys(validResponses).forEach(questionId => {
            dirtyQuestionIdsRef.current.delete(String(questionId));
          });
          logger.debug('Auto-save completed', { saved: result.saved, skipped: result.skipped });
          // Reset auto-save failed state on success
          if (autoSaveFailed) {
            setAutoSaveFailed(false);
          }
        } else if (result.failed > 0) {
          logger.debug('Auto-save completed', { 
            saved: result.saved, 
            skipped: result.skipped, 
            failed: result.failed 
          });
          setAutoSaveFailed(true);
          // Don't show alert for auto-save failures (too intrusive)
          // User can see status indicators and retry manually
        }
        
        periodicSaveTimeoutRef.current = null;
      } catch (err) {
        // FIX 1: Check if component is mounted before state update
        if (isMountedRef.current) {
          logger.error('Error during periodic save', { error: err });
          setAutoSaveFailed(true);
        }
        periodicSaveTimeoutRef.current = null;
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => {
      // FIX 2: Clear timeout on cleanup
      if (periodicSaveTimeoutRef.current) {
        clearTimeout(periodicSaveTimeoutRef.current);
        periodicSaveTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveEnabled, autoSaveFailed, quiz, hasValidAnswer, getDirtyValidResponses]);

  // Auto-save for timed quizzes - periodic saves every 30 seconds (backup mechanism)
  // Note: Immediate saves happen in handleResponseChange, this is just a backup
  // FIX 3: Use ref to track interval and prevent multiple intervals
  useEffect(() => {
    const isTimedQuiz = timerDeadlineMs !== null && timerDeadlineMs > Date.now();
    const hasTimeLimit = attempt?.assignment?.time_limit_minutes && attempt.assignment.time_limit_minutes > 0;
    
    if (!isTimedQuiz || !hasTimeLimit || !saveManagerRef.current) {
      // FIX 3: Clear interval if conditions not met
      if (timedSaveIntervalRef.current) {
        clearInterval(timedSaveIntervalRef.current);
        timedSaveIntervalRef.current = null;
      }
      return;
    }
    
    // FIX 3: Clear existing interval before creating new one
    if (timedSaveIntervalRef.current) {
      clearInterval(timedSaveIntervalRef.current);
      timedSaveIntervalRef.current = null;
    }
    
    timedSaveIntervalRef.current = setInterval(() => {
      // FIX 1: Check if component is mounted
      if (!isMountedRef.current) {
        return;
      }

      if (dirtyQuestionIdsRef.current.size === 0) {
        return;
      }
      
      // Double-check conditions before saving
      if (!saveManagerRef.current) {
        logger.warn('Save manager not available during timed quiz periodic save');
        return;
      }
      
      const validResponses = getDirtyValidResponses();
      
      if (Object.keys(validResponses).length === 0) {
        logger.debug('No valid responses to save in timed quiz periodic save');
        return;
      }
      
      logger.debug('Starting periodic backup save for timed quiz', { 
        dirtyResponses: dirtyQuestionIdsRef.current.size,
        validResponses: Object.keys(validResponses).length 
      });
      
      saveManagerRef.current.saveAllResponses(validResponses, {
        skipUnchanged: true, // Only save changed questions
        skipVerification: true, // Faster for periodic saves
        onProgress: (questionId, success) => {
          // FIX 1: Check if component is mounted before state update
          if (!isMountedRef.current) return;
          
          if (!success) {
            logger.warn('Failed to save question during timed quiz auto-save', { questionId });
            // Update save status
            setSaveStatuses(prev => ({
              ...prev,
              [questionId]: {
                status: 'failed',
                timestamp: Date.now(),
                error: 'Periodic save failed'
              }
            }));
          } else {
            dirtyQuestionIdsRef.current.delete(String(questionId));
          }
        }
      }).then(result => {
        // FIX 1: Check if component is mounted before logging
        if (!isMountedRef.current) return;
        
        if (result.success) {
          Object.keys(validResponses).forEach(questionId => {
            dirtyQuestionIdsRef.current.delete(String(questionId));
          });
          logger.debug('Timed quiz auto-save completed', { saved: result.saved, skipped: result.skipped });
        } else if (result.failed > 0) {
          logger.error('Timed quiz auto-save failed for some responses', { failed: result.failed });
        }
      }).catch(err => {
        // FIX 1: Check if component is mounted before logging
        if (isMountedRef.current) {
          logger.error('Error during timed quiz periodic save', { error: err });
        }
      });
    }, 30000); // Every 30 seconds (backup mechanism)

    return () => {
      // FIX 3: Clear interval on cleanup
      if (timedSaveIntervalRef.current) {
        clearInterval(timedSaveIntervalRef.current);
        timedSaveIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerDeadlineMs, attempt, attemptId, getDirtyValidResponses]); // responses not needed - responsesRef is updated by separate useEffect when responses changes

  const getBlankCount = (prompt) => {
    if (!prompt) return 0;
    const matches = prompt.match(/_{1,}/g);
    return matches ? matches.length : 0;
  };

  const handleResponseChange = (questionId, value, type, blankIndex = null) => {
    // UI FIX 3: Disable interactions during auto-submit
    if (isAutoSubmitting) {
      logger.warn('Cannot change answer during auto-submit');
      return;
    }

    // DEBUG: Log response change for save issue analysis
    logger.debug('Response change triggered', {
      questionId,
      type,
      value: type === 'fill_blank' ? '[REDACTED]' : value,
      blankIndex,
      timestamp: Date.now()
    });
    // CRITICAL FIX: Read from ref first (always current), fallback to state
    // This prevents race conditions when user rapidly changes answers
    const currentResponse = responsesRef.current?.[questionId] || responses[questionId];
    // Initialize newResponse with proper structure, ensuring all fields are defined
    const newResponse = currentResponse ? { ...currentResponse } : {
      selected_option_id: null,
      selected_option_ids: null,
      answer_text: null,
      answer_texts: null
    };
    
    if (type === 'multiple_choice') {
      // FIX #4: Standardized multiple choice answer handling
      // Primary source of truth: selected_option_ids array
      // Other fields are derived for backward compatibility
      let selectedOptionIds = [];
      
      // FIX #4: Read from selected_option_ids first (primary source)
      if (newResponse.selected_option_ids && Array.isArray(newResponse.selected_option_ids)) {
        selectedOptionIds = [...newResponse.selected_option_ids];
      } else if (newResponse.answer_text) {
        // Fallback: Try to parse from answer_text (for backward compatibility)
        try {
          const parsed = JSON.parse(newResponse.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, might be old format
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && newResponse.selected_option_id) {
        selectedOptionIds = [newResponse.selected_option_id];
      }
      
      // Toggle the option
      const optionIndex = selectedOptionIds.indexOf(value);
      if (optionIndex > -1) {
        // Remove if already selected
        selectedOptionIds = selectedOptionIds.filter(id => id !== value);
      } else {
        // Add if not selected
        selectedOptionIds = [...selectedOptionIds, value];
      }
      
      // FIX #4: selected_option_ids is the primary source of truth
      newResponse.selected_option_ids = selectedOptionIds;
      
      // FIX #4: Derive other fields from selected_option_ids for backward compatibility
      // answer_text: JSON array for API/storage
      newResponse.answer_text = selectedOptionIds.length > 0 ? JSON.stringify(selectedOptionIds) : null;
      // selected_option_id: First selected option for legacy API compatibility
      newResponse.selected_option_id = selectedOptionIds.length > 0 ? selectedOptionIds[0] : null;
      newResponse.answer_texts = null;
    } else if (type === 'true_false') {
      newResponse.selected_option_id = value;
      newResponse.selected_option_ids = null;
      newResponse.answer_text = null;
      newResponse.answer_texts = null;
    } else if (type === 'fill_blank') {
      // FIX #6: Escape pipe characters in user input to prevent parsing issues
      const PIPE_ESCAPE = '\u001F'; // Unit separator character
      const escapePipe = (text) => {
        if (!text) return text;
        return text.replace(/\|/g, PIPE_ESCAPE);
      };
      
      if (blankIndex !== null) {
        // Multiple blanks - update specific blank
        // CRITICAL FIX: Ensure answer_texts is properly initialized as an array
        const answerTexts = Array.isArray(newResponse.answer_texts)
          ? [...newResponse.answer_texts]
          : [];
        // Ensure array is large enough for the blankIndex
        while (answerTexts.length <= blankIndex) {
          answerTexts.push('');
        }
        answerTexts[blankIndex] = value;
        newResponse.answer_texts = answerTexts;
        // FIX #6: Escape pipes in individual answers before joining
        newResponse.answer_text = answerTexts.map(escapePipe).join('|'); // Pipe-separated for backend
      } else {
        // Single blank - use original logic
        // FIX #6: Escape pipes in single answer
        newResponse.answer_text = escapePipe(value || '');
        newResponse.answer_texts = [value || ''];
      }
      newResponse.selected_option_id = null;
      newResponse.selected_option_ids = null;
    } else if (type === 'matching') {
      // value should be { leftIndex, rightIndex } or null to remove a pair
      // CRITICAL FIX: Ensure pairs array is properly initialized
      let pairs = [];
      try {
        if (newResponse.answer_text && typeof newResponse.answer_text === 'string') {
          const parsed = JSON.parse(newResponse.answer_text);
          if (Array.isArray(parsed)) {
            pairs = parsed;
          }
        }
      } catch (e) {
        // Invalid JSON or not an array - start with empty array
        pairs = [];
      }
      
      if (value === null) {
        // Remove pair for leftIndex
        const leftIndex = blankIndex; // Using blankIndex to pass leftIndex
        pairs = pairs.filter(p => p.leftIndex !== leftIndex);
      } else {
        // Add or update pair
        // CRITICAL FIX: Validate value structure before destructuring
        if (value && typeof value === 'object' && 'leftIndex' in value) {
          const { leftIndex, rightIndex } = value;
          pairs = pairs.filter(p => p.leftIndex !== leftIndex);
          if (rightIndex !== null && rightIndex !== undefined) {
            pairs.push({ leftIndex, rightIndex });
          }
        }
      }
      
      newResponse.answer_text = JSON.stringify(pairs);
      newResponse.selected_option_id = null;
      newResponse.selected_option_ids = null;
      newResponse.answer_texts = null;
    }
    
    // FIX #1: Improved state update to prevent race conditions
    // Update ref BEFORE state to ensure it's always current
    // This prevents race conditions when multiple rapid answer changes occur
    const updatedResponses = { ...responsesRef.current, [questionId]: newResponse };
    responsesRef.current = updatedResponses;
    dirtyQuestionIdsRef.current.add(String(questionId));
    
    // Then update state (will trigger re-render)
    setResponses(updatedResponses);
    
    // IMMEDIATE AUTO-SAVE: Save immediately when answer changes (for both timed and untimed quizzes)
    // CRITICAL FIX: Add comprehensive null checks and better error handling
    if (!saveManagerRef.current) {
      logger.warn('Save manager not initialized for question. Save will be queued.', { questionId });
      // Queue the save attempt - it will be processed when save manager is ready
      // The periodic auto-save will pick it up
      return;
    }

    // CRITICAL FIX: Get question type and validate that answer is actually selected/entered
    // Only save if there's a valid answer - don't save empty responses
    const question = quiz?.questions?.find(q => q.id === questionId);

    // DEBUG: Log auto-save initiation
    logger.debug('Auto-save initiated for question', {
      questionId,
      type,
      hasValidAnswer: hasValidAnswer(question?.type, newResponse),
      timestamp: Date.now()
    });
    if (!question) {
      logger.warn('Question not found for validation', { questionId });
      return;
    }
    
    // Use question-type-specific validation to ensure answer is actually provided
    if (!hasValidAnswer(question.type, newResponse)) {
      logger.debug('Skipping save for question: No valid answer selected/entered', { 
        questionId, 
        questionType: question.type,
        response: newResponse 
      });
      return;
    }
    
    // Clear any existing debounce timer for this question
    const existingTimer = debounceTimersRef.current[questionId];
    if (existingTimer) {
      clearTimeout(existingTimer);
      // Timer was cleared, but keep 'saving' status since we're setting a new one
    }
    
    // CRITICAL FIX: Set save status to 'saving' IMMEDIATELY when timer is set
    // This ensures hasUnsavedAnswers detects it right away (fixes race condition)
    // This is especially important for the last question when user clicks "Review Answers"
    // FIX: Only set status if not already saving to prevent conflicts with save manager
    const timerStartTime = Date.now();
    if (isMountedRef.current) {
      setSaveStatuses(prev => {
        const currentStatus = prev[questionId];
        // Don't override if already saving (prevents conflicts with manual saves)
        if (currentStatus?.status === 'saving') {
          return prev;
        }
        return {
          ...prev,
          [questionId]: {
            status: 'saving',
            timestamp: timerStartTime,
            error: null
          }
        };
      });
    }

    const normalizedType = (question.type || type || '').toLowerCase();
    const debounceDelay = ['multiple_choice', 'true_false', 'single_choice'].includes(normalizedType)
      ? 500
      : 1800;

    // Debounce immediate save to avoid too many requests. Text-like answers wait longer
    // because each keystroke can produce a response change event.
    debounceTimersRef.current[questionId] = setTimeout(() => {
      // Use the latest response from ref to avoid stale closures
      const latestResponse = responsesRef.current?.[questionId] || newResponse;
      
      // Double-check response validity before saving using question type
      // CRITICAL FIX: Get question type and validate that answer is actually selected/entered
      const latestQuestion = quiz?.questions?.find(q => q.id === questionId);
      if (!latestQuestion) {
        logger.warn('Question not found for validation in debounced save', { questionId });
        delete debounceTimersRef.current[questionId];
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            if (updated[questionId]?.status === 'saving') {
              delete updated[questionId];
            }
            return updated;
          });
        }
        return;
      }
      
      // Use question-type-specific validation to ensure answer is actually provided
      if (!hasValidAnswer(latestQuestion.type, latestResponse)) {
        logger.debug('Skipping save for question: Response became invalid or no answer selected/entered', {
          questionId,
          questionType: latestQuestion.type,
          response: latestResponse
        });
        delete debounceTimersRef.current[questionId];
        // Clear 'saving' status since we're not saving
        // FIX: Only clear if this timer is still the active one (not overridden by newer timer)
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            const currentStatus = updated[questionId];
            // Only clear if status is 'saving' and timestamp matches our timer start
            if (currentStatus?.status === 'saving' && currentStatus?.timestamp === timerStartTime) {
              delete updated[questionId];
            }
            return updated;
          });
        }
        return;
      }
      
      // Ensure save manager is still available
      if (!saveManagerRef.current) {
        logger.warn('Save manager became unavailable for question', { questionId });
        delete debounceTimersRef.current[questionId];
        // Clear 'saving' status since we can't save
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            if (updated[questionId]?.status === 'saving') {
              updated[questionId] = {
                status: 'failed',
                timestamp: Date.now(),
                error: 'Save manager unavailable'
              };
            }
            return updated;
          });
        }
        return;
      }

      // FIX: Check if manual save is already in progress to prevent duplicate saves
      if (manualSaveFlagsRef.current[questionId]) {
        logger.debug('Skipping auto-save: manual save in progress', { questionId });
        delete debounceTimersRef.current[questionId];
        return;
      }
      
      // FIX: Check component mount state before saving
      if (!isMountedRef.current) {
        logger.debug('Skipping auto-save: component unmounted', { questionId });
        delete debounceTimersRef.current[questionId];
        return;
      }

      logger.debug('Saving question immediately after answer change', {
        questionId,
        hasResponse: !!latestResponse,
        timestamp: Date.now()
      });

      saveManagerRef.current.saveResponse(questionId, latestResponse, {
        skipVerification: true, // Faster for auto-save
        maxRetries: 2
      }).then(result => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) return;
        
        if (result.success) {
          logger.debug('Successfully saved question', { questionId });
          dirtyQuestionIdsRef.current.delete(String(questionId));
          // FIX 8: Reset auto-save failed state on immediate save success
          if (autoSaveFailed) {
            setAutoSaveFailed(false);
          }
        } else if (result.cancelled) {
          logger.debug('Save cancelled for question (newer save in progress)', { questionId });
        } else {
          logger.warn('Failed to save question', { questionId, error: result.error });
          // Update save status to show failure
          setSaveStatuses(prev => ({
            ...prev,
            [questionId]: {
              status: 'failed',
              timestamp: Date.now(),
              error: result.error?.message || 'Save failed'
            }
          }));
        }
      }).catch(err => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) return;
        
        logger.error('Error saving question', { questionId, error: err });
        // Update save status to show failure
        setSaveStatuses(prev => ({
          ...prev,
          [questionId]: {
            status: 'failed',
            timestamp: Date.now(),
            error: err?.message || 'Save failed'
          }
        }));
      });
      
      delete debounceTimersRef.current[questionId];
    }, debounceDelay);
  };

  // Save current question before navigating away
  // CRITICAL: Must be defined before handleSubmit to avoid initialization error
  const saveCurrentQuestionBeforeNavigation = useCallback(async () => {
    if (!saveManagerRef.current || !quiz?.questions || currentQuestionIndex < 0) {
      return;
    }

    // For submitted attempts in partial edit/retake mode, avoid extra navigation-save calls.
    // Immediate answer-change saves and submit-time saves already handle persistence, and
    // navigation-triggered duplicate saves can hit permission edge cases.
    if (attempt?.submitted_at) {
      return;
    }
    
    const currentQuestion = quiz.questions[currentQuestionIndex];
    if (!currentQuestion) {
      return;
    }
    
    // PRIORITY 1 FIX: Use both state and ref, prefer state (more current)
    // This ensures we get the latest answer even if user just changed it
    const currentResponse = responses[currentQuestion.id] || responsesRef.current?.[currentQuestion.id];
    if (!currentResponse) {
      return;
    }
    
    // CRITICAL FIX: Use question-type-specific validation to ensure answer is actually selected/entered
    // Only save if there's a valid answer - don't save empty responses
    if (!hasValidAnswer(currentQuestion.type, currentResponse)) {
      logger.debug('Skipping save before navigation: No valid answer selected/entered', { 
        questionId: currentQuestion.id,
        questionType: currentQuestion.type,
        response: currentResponse 
      });
      return;
    }
    
    // FIX 5: Clear all pending debounce timers before navigation save
    // Also clear the corresponding saveStatuses to prevent stuck 'saving' states
    const clearedQuestionIds = [];
    Object.keys(debounceTimersRef.current).forEach(questionId => {
      const timer = debounceTimersRef.current[questionId];
      if (timer) {
        clearTimeout(timer);
        delete debounceTimersRef.current[questionId];
        clearedQuestionIds.push(questionId);
      }
    });
    
    // Clear saveStatuses for questions whose timers were cleared
    // This prevents the 'saving' status from being stuck indefinitely
    if (clearedQuestionIds.length > 0 && isMountedRef.current) {
      setSaveStatuses(prev => {
        const updated = { ...prev };
        clearedQuestionIds.forEach(qId => {
          // Only clear if status is 'saving' (don't clear 'saved' or 'failed')
          if (updated[qId]?.status === 'saving') {
            delete updated[qId];
          }
        });
        return updated;
      });
    }
    logger.debug('Cleared all pending debounced saves before navigation', { clearedQuestionIds });
    
    // Check if response has changed (don't save if already saved)
    // Note: hasResponseChanged is a method, so we need to call it correctly
    try {
      if (saveManagerRef.current && typeof saveManagerRef.current.hasResponseChanged === 'function') {
        if (!saveManagerRef.current.hasResponseChanged(currentQuestion.id, currentResponse)) {
          logger.debug('Question already saved, skipping', { questionId: currentQuestion.id });
          return;
        }
      }
    } catch (err) {
      // If check fails, proceed with save (better safe than sorry)
      logger.warn('Could not check if response changed', { error: err });
    }
    
    // FIX 9: Save immediately and wait for completion (with timeout to prevent blocking)
    try {
      // Set a timeout to prevent blocking navigation too long
      const savePromise = saveManagerRef.current.saveResponse(currentQuestion.id, currentResponse, {
        skipVerification: true,
        maxRetries: 1  // Quick retry, don't wait too long
      });
      
      // Race between save and timeout (max 3 seconds)
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ timeout: true }), 3000);
      });
      
      const result = await Promise.race([savePromise, timeoutPromise]);
      
      if (result.timeout) {
        logger.warn('Save timed out for question, proceeding with navigation', { questionId: currentQuestion.id });
        // Don't block navigation - the periodic save will catch it
      } else if (result.success) {
        logger.debug('Successfully saved question before navigation', { questionId: currentQuestion.id });
      } else if (result.cancelled) {
        logger.debug('Save cancelled for question (newer save in progress)', { questionId: currentQuestion.id });
      } else {
        logger.warn('Failed to save question before navigation', { questionId: currentQuestion.id, error: result.error });
      }
    } catch (err) {
      logger.warn('Error saving question before navigation', { questionId: currentQuestion.id, error: err });
      // Don't block navigation - the periodic save will catch it
    }
  }, [currentQuestionIndex, quiz, responses, hasValidAnswer, attempt]); // PRIORITY 3 FIX: Add responses to dependencies to ensure latest data

  const queueCurrentQuestionSaveForNavigation = useCallback(() => {
    if (!saveManagerRef.current || !quiz?.questions || currentQuestionIndex < 0) {
      return;
    }

    // Submitted partial edit/retake attempts rely on direct answer-change saves and submit-time saves.
    if (attempt?.submitted_at) {
      return;
    }

    const currentQuestion = quiz.questions[currentQuestionIndex];
    if (!currentQuestion) {
      return;
    }

    const currentResponse = responsesRef.current?.[currentQuestion.id] || responses[currentQuestion.id];
    if (!currentResponse || !hasValidAnswer(currentQuestion.type, currentResponse)) {
      return;
    }

    const pendingTimer = debounceTimersRef.current[currentQuestion.id];
    if (pendingTimer) {
      logger.debug('Navigation save skipped: debounce save already pending', { questionId: currentQuestion.id });
      return;
    }

    try {
      if (typeof saveManagerRef.current.hasResponseChanged === 'function' &&
          !saveManagerRef.current.hasResponseChanged(currentQuestion.id, currentResponse)) {
        return;
      }
    } catch (err) {
      logger.warn('Could not check if response changed before background navigation save', { error: err });
    }

    saveManagerRef.current.saveResponse(currentQuestion.id, currentResponse, {
      skipVerification: true,
      maxRetries: 1
    }).then(result => {
      if (!isMountedRef.current) return;

      if (result.success) {
        logger.debug('Background navigation save completed', { questionId: currentQuestion.id });
        dirtyQuestionIdsRef.current.delete(String(currentQuestion.id));
      } else if (result.cancelled) {
        logger.debug('Background navigation save cancelled by newer save', { questionId: currentQuestion.id });
      } else {
        logger.warn('Background navigation save failed', { questionId: currentQuestion.id, error: result.error });
      }
    }).catch(err => {
      if (!isMountedRef.current) return;
      logger.warn('Background navigation save errored', { questionId: currentQuestion.id, error: err });
    });
  }, [currentQuestionIndex, quiz, responses, hasValidAnswer, attempt]);

  const saveResponse = async (questionId) => {
    // DEBUG: Log manual save attempt
    logger.debug('Manual save initiated', {
      questionId,
      hasResponse: !!(responses[questionId] || responsesRef.current[questionId]),
      hasSaveManager: !!saveManagerRef.current,
      timestamp: Date.now()
    });

    // PRIORITY 1 FIX: Cancel any pending auto-save for this question
    if (debounceTimersRef.current[questionId]) {
      clearTimeout(debounceTimersRef.current[questionId]);
      delete debounceTimersRef.current[questionId];
      logger.debug('Cancelled pending auto-save for question', { questionId });
    }

    // PRIORITY 4 FIX: Use both state and ref, prefer state (more current)
    const response = responses[questionId] || responsesRef.current[questionId];
    if (!response || !saveManagerRef.current) {
      logger.warn('Save aborted: missing response or save manager', {
        questionId,
        hasResponse: !!response,
        hasSaveManager: !!saveManagerRef.current
      });
      return;
    }

    try {
      setSaving(true);
      
      // PRIORITY 2 FIX: Track this as a manual save for extended status timeout
      manualSaveFlagsRef.current[questionId] = true;
      
      logger.debug('Saving response for question', { questionId });
      
      const result = await saveManagerRef.current.saveResponse(questionId, response, {
        skipVerification: false,
        maxRetries: 3
      });
      
      // FIX 1: Check if component is mounted before state update
      if (!isMountedRef.current) {
        return;
      }
      
      if (result.success) {
        logger.debug('Successfully saved response for question', { questionId });
        dirtyQuestionIdsRef.current.delete(String(questionId));
        // Reset auto-save failed state if manual save succeeds
        if (autoSaveFailed) {
          setAutoSaveFailed(false);
        }
        // Flag will be cleared in status callback when status becomes 'saved'
      } else if (!result.cancelled) {
        // Only show error if not cancelled (cancelled means newer save is in progress)
        // Use enhanced error message from save manager
        const errorMsg = result.error?.message || 'Failed to save your answer. Please check your internet connection and try again.';
        // Clear manual save flag on error (PRIORITY 1 FIX)
        delete manualSaveFlagsRef.current[questionId];
        
        // Show appropriate error message based on error type
        if (result.error?.isServiceUnavailable) {
          toast.warning(`${errorMsg}\n\nYour answer is queued and will be saved automatically when the service is available.`, { duration: 9000 });
        } else if (result.error?.isNetworkError) {
          toast.warning(`${errorMsg}\n\nYour answer is queued and will be saved automatically when your connection returns.`, { duration: 9000 });
        } else if (result.error?.isServerError) {
          toast.error(`${errorMsg}\n\nPlease try saving again in a moment.`);
        } else if (result.error?.isAuthError) {
          toast.error(`${errorMsg}\n\nPlease refresh the page and log in again.`);
        } else if (result.error?.csrfTokenMissing) {
          toast.error(`${errorMsg}\n\nThis may be due to a network issue. Please check your connection and try again, or refresh the page.`);
        } else {
          toast.error(errorMsg);
        }
      } else {
        // Save was cancelled (newer save in progress)
        // Clear manual save flag (PRIORITY 2 FIX)
        delete manualSaveFlagsRef.current[questionId];
      }
    } catch (err) {
      logger.error('Error saving response for question', { questionId, error: err });
      // Clear manual save flag on error (PRIORITY 1 FIX)
      delete manualSaveFlagsRef.current[questionId];
      toast.error('Failed to save your answer. Please check your internet connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = useCallback(async (isAutoSubmit = false) => {
    // CRITICAL FIX: For auto-submit, don't block on saving flag
    // We'll wait for saves to complete below, but we must proceed with submission
    // even if saves are in progress to prevent timer expiration without submission
    if (!isAutoSubmit && saving) return;

    setSaving(true);
    setError(null);

    try {
      // CRITICAL FIX: Clear any stale 'saving' statuses before submission
      // This prevents blocking submission due to stale statuses that haven't been cleared
      setSaveStatuses(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        // Clear any 'saving' statuses that are older than 2 seconds (likely stale)
        const now = Date.now();
        Object.keys(updated).forEach(questionId => {
          const status = updated[questionId];
          if (status?.status === 'saving' && status?.timestamp) {
            const age = now - status.timestamp;
            // If status is older than 2 seconds, it's likely stale - mark as saved
            if (age > 2000) {
              updated[questionId] = {
                status: 'saved',
                timestamp: now,
                error: null
              };
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      // FIX 1: Clear all pending debounce timers FIRST to ensure all answers are included
      Object.keys(debounceTimersRef.current).forEach(questionId => {
        const timer = debounceTimersRef.current[questionId];
        if (timer) {
          clearTimeout(timer);
          delete debounceTimersRef.current[questionId];
        }
      });
      logger.debug('Cleared all pending debounced saves before submission');

      // CRITICAL FIX: If in review mode, save current question before submission
      // This ensures any edits made in review mode are saved
      if (isReviewMode && currentQuestionIndex >= 0 && saveManagerRef.current && quiz?.questions) {
        await saveCurrentQuestionBeforeNavigation();
      }

      // FIX #9: Improved auto-submit handling with longer timeout and final save attempt
      // Wait for any active saves to complete before submitting
      // For auto-submit, use longer timeout (8 seconds) to give saves more time to complete
      const saveTimeout = isAutoSubmit ? 8000 : 5000;
      if (saveManagerRef.current) {
        const savesCompleted = await saveManagerRef.current.waitForSaves(saveTimeout);
        if (!savesCompleted) {
          logger.warn('Some saves may not have completed before submission', { timeout: saveTimeout });
          
          // FIX #9: For auto-submit, make one final attempt to save all pending responses
          if (isAutoSubmit) {
            logger.warn('Auto-submit: Making final save attempt before proceeding');
            
            // Get all responses that haven't been saved yet
            const allResponses = responsesRef.current || responses;
            const unsavedResponses = {};
            
            if (quiz?.questions) {
              for (const [questionId, response] of Object.entries(allResponses)) {
                const question = quiz.questions.find(q => q.id === questionId);
                if (question && response && hasValidAnswer(question.type, response)) {
                  // Check if this response has been saved
                  const saveStatus = saveManagerRef.current.getSaveStatus(questionId);
                  if (saveStatus.status !== 'saved') {
                    unsavedResponses[questionId] = response;
                  }
                }
              }
            }
            
            if (Object.keys(unsavedResponses).length > 0) {
              logger.warn('Auto-submit: Attempting to save unsaved responses', {
                count: Object.keys(unsavedResponses).length
              });
              
              // Try to save with a short timeout (2 seconds per response, max 10 seconds total)
              try {
                await Promise.race([
                  saveManagerRef.current.saveAllResponses(unsavedResponses, {
                    skipVerification: true, // Skip verification for speed
                    skipUnchanged: false // Force save even if marked as unchanged
                  }),
                  new Promise(resolve => setTimeout(resolve, 10000)) // 10 second max
                ]);
              } catch (finalSaveError) {
                logger.error('Auto-submit: Final save attempt failed', { error: finalSaveError });
              }
            }
            
            logger.warn('Auto-submit proceeding to prevent timer expiration');
          }
        }
      }

      // Save all responses before submitting using save manager
      const saveErrors = [];

      if (saveManagerRef.current) {
        // FIX 2: Use both state and ref to get latest answers (prevents race condition)
        // This ensures we capture answers even if state update is pending
        const allResponses = responsesRef.current || responses;
        
        // CRITICAL FIX: Use question-type-specific validation to filter responses
        // Only save responses that have actual answers selected/entered
        const responsesToSave = {};
        if (quiz?.questions) {
          for (const [questionId, response] of Object.entries(allResponses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (question && response && hasValidAnswer(question.type, response)) {
              responsesToSave[questionId] = response;
            }
          }
        }
        
        if (Object.keys(responsesToSave).length > 0) {
          const responseCount = Object.keys(responsesToSave).length;

          const saveAllPromise = saveManagerRef.current.saveAllResponses(responsesToSave, {
            skipVerification: false
          });

          // Prevent indefinite "Submitting..." if save manager hangs on a response.
          // Use a dynamic timeout so larger edited sets have enough time to persist.
          const saveAllTimeoutMs = isAutoSubmit
            ? Math.min(25000, Math.max(12000, responseCount * 1500))
            : Math.min(45000, Math.max(15000, responseCount * 2000));
          const timeoutResult = { timeout: true, failed: responseCount, results: [] };
          const result = await Promise.race([
            saveAllPromise,
            new Promise((resolve) => setTimeout(() => resolve(timeoutResult), saveAllTimeoutMs))
          ]);

          if (result?.timeout) {
            if (!isAutoSubmit) {
              setError('Saving answers is taking too long. Please wait a moment and submit again.');
              setSaving(false);
              logger.error('Submission blocked due to save timeout', { timeoutMs: saveAllTimeoutMs, responseCount: Object.keys(responsesToSave).length });
              return;
            }

            logger.warn('Auto-submit: saveAllResponses timed out, proceeding to avoid missing submission', {
              timeoutMs: saveAllTimeoutMs,
              responseCount: Object.keys(responsesToSave).length
            });
          }
          
          // FIX 3: Verify saves completed before allowing submission
          // Check for failures
          let hasFailures = false;
          if (result.results) {
            for (const resultItem of result.results) {
              if (!resultItem.success && !resultItem.skipped) {
                saveErrors.push(resultItem.questionId);
                hasFailures = true;
              }
            }
          } else if (result.failed > 0) {
            // If results array not available, check failed count
            hasFailures = true;
            logger.warn('Some responses failed to save', { failed: result.failed });
          }
          
          // FIX 3: Block submission if saves failed (unless auto-submit)
          // For auto-submit, proceed anyway to prevent timer expiration without submission
          if (hasFailures && !isAutoSubmit) {
            const errorMessage = saveErrors.length > 0
              ? `Failed to save ${saveErrors.length} answer(s) before submission. Please try again.`
              : `Failed to save some answers. Please try again.`;
            setError(errorMessage);
            setSaving(false);
            logger.error('Submission blocked due to save failures', { saveErrors });
            return; // Don't proceed with submission
          } else if (hasFailures && isAutoSubmit) {
            logger.warn('Auto-submit proceeding despite save failures to prevent timer expiration');
          }
        }
      } else {
        // Fallback to direct API calls if save manager not available
        // FIX 2: Use both state and ref in fallback too
        const allResponses = responsesRef.current || responses;
        
        for (const [questionId, response] of Object.entries(allResponses)) {
          if (response && (response.selected_option_id || response.answer_text)) {
            try {
              // Ensure answer_text is properly set for multiple choice questions
              let answerText = response.answer_text || '';
              if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
                // If answer_text is missing but selected_option_ids exists, convert it
                answerText = JSON.stringify(response.selected_option_ids);
              }
              
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: answerText
              });
            } catch (saveErr) {
              logger.error('Failed to save response for question', { questionId, error: saveErr });
              saveErrors.push(questionId);
              // Continue saving other responses even if one fails
            }
          }
        }
      }

      // FIX 3: Block submission if saves failed (for fallback path too, unless auto-submit)
      if (saveErrors.length > 0 && !isAutoSubmit) {
        const errorMessage = `Failed to save ${saveErrors.length} answer(s) before submission. Please try again.`;
        setError(errorMessage);
        setSaving(false);
        logger.error('Submission blocked due to save failures', { saveErrors });
        return; // Don't proceed with submission
      } else if (saveErrors.length > 0 && isAutoSubmit) {
        logger.warn('Auto-submit proceeding despite save failures to prevent timer expiration');
      }

      // CRITICAL FIX 1: Use auto-submit endpoint for single-attempt quizzes when auto-submitting
      // Submit the attempt - use auto-submit endpoint for single-attempt quizzes when auto-submitting
      const response = isAutoSubmit && attempt?.assignment?.attempts_allowed === 1
        ? await quizApiService.autoSubmitAttempt(attemptId)
        : await quizApiService.submitAttempt(attemptId);
      
      if (response.success) {
        // Guard: auto-submit endpoint can return a non-final "progress saved" response.
        // In that case, do NOT navigate to results yet; throw to trigger retry flow.
        if (isAutoSubmit && response?.data?.auto_submitted === false) {
          throw new Error('Auto-submit not finalized yet. Retrying...');
        }

        // FIX 8: Clear error state on successful submission
        if (isMountedRef.current) {
          setError(null);
        }
        
        // CRITICAL FIX: Always navigate to results page after submission
        // The results page will handle showing appropriate message if immediate_feedback is false
        navigate(`/student/result/${attemptId}`, { 
          state: { results: response.data } 
        });
        
        // CRITICAL FIX 2: Return success for auto-submit to enable retry logic
        if (isAutoSubmit) {
          return { success: true };
        }
      } else {
        const errorMsg = response.message || response.error || 'Failed to submit quiz';
        setError(errorMsg);
        // CRITICAL FIX 2: Throw error for auto-submit to enable retry logic
        if (isAutoSubmit) {
          throw new Error(errorMsg);
        }
      }
    } catch (err) {
      logger.error('Error submitting quiz', { error: err, attemptId });
      const errorMsg = getApiErrorMessage(err, 'Failed to submit quiz. Please try again.');
      setError(errorMsg);
      // CRITICAL FIX 2: Re-throw error for auto-submit to enable retry logic
      if (isAutoSubmit) {
        throw err;
      }
    } finally {
      setSaving(false);
    }
  }, [attemptId, responses, navigate, saving, isReviewMode, currentQuestionIndex, quiz, saveCurrentQuestionBeforeNavigation, attempt, hasValidAnswer]);

  // MEDIUM PRIORITY FIX 3: Add retry logic for failed auto-submit
  const submitWithRetry = useCallback(async (maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        logger.debug('Auto-Submit attempt', { attempt: i + 1, maxRetries });
        // CRITICAL FIX 2: handleSubmit now throws errors for auto-submit, so retry will work
        const result = await handleSubmit(true); // Pass isAutoSubmit flag
        logger.info('Auto-Submit successfully submitted', { attemptId });
        return result; // Success
      } catch (error) {
        logger.error('Auto-Submit attempt failed', { attempt: i + 1, maxRetries, error });
        if (i < maxRetries - 1) {
          const delay = 1000 * Math.pow(2, i); // Exponential backoff: 1s, 2s, 4s
          logger.warn('Auto-Submit retrying', { delay, attempt: i + 1 });
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          logger.error('Auto-Submit all retries failed', { maxRetries });
          // Show error to user
          // FIX 5: Check mount before state update
          if (isMountedRef.current) {
            setError('Failed to auto-submit quiz. Please submit manually.');
            setSaving(false);
            setIsAutoSubmitting(false); // FIX 1: Use state as single source of truth
          }
          throw error;
        }
      }
    }
  }, [handleSubmit, setError, setSaving, attemptId]);

  // Set the ref value for handleSubmit
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // FIX 5: Set up mount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      if (autoSubmitCountdownIntervalRef.current) {
        clearInterval(autoSubmitCountdownIntervalRef.current);
      }
      isMountedRef.current = false;
    };
  }, []);

  // State for auto-submit handling
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [showAutoSubmitCountdownModal, setShowAutoSubmitCountdownModal] = useState(false);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(AUTO_SUBMIT_MODAL_SECONDS);
  const autoSubmitCountdownIntervalRef = useRef(null);
  
  // Handle auto-submit when timer expires
  const handleTimerExpired = useCallback(async () => {
    logger.info('Timer expired (useRealTimeCountdown), starting auto-submit process', { attemptId });
    
    // Don't submit if already submitting
    if (isAutoSubmitting) {
      logger.warn('Auto-submit already in progress, skipping duplicate');
      return;
    }
    
    setIsAutoSubmitting(true);
    
    try {
      const isTimedSingleAttemptQuiz =
        timerDeadlineMs !== null &&
        (attempt?.assignment?.time_limit_minutes && attempt.assignment.time_limit_minutes > 0) &&
        attempt?.assignment?.attempts_allowed === 1;

      // Show 10-second warning modal ONLY for timed single-attempt quizzes.
      if (isTimedSingleAttemptQuiz) {
        setAutoSubmitCountdown(AUTO_SUBMIT_MODAL_SECONDS);
        setShowAutoSubmitCountdownModal(true);

        await new Promise((resolve) => {
          let remaining = AUTO_SUBMIT_MODAL_SECONDS;

          autoSubmitCountdownIntervalRef.current = setInterval(() => {
            remaining -= 1;

            if (!isMountedRef.current) {
              clearInterval(autoSubmitCountdownIntervalRef.current);
              autoSubmitCountdownIntervalRef.current = null;
              resolve();
              return;
            }

            setAutoSubmitCountdown(Math.max(0, remaining));

            if (remaining <= 0) {
              clearInterval(autoSubmitCountdownIntervalRef.current);
              autoSubmitCountdownIntervalRef.current = null;
              resolve();
            }
          }, 1000);
        });
      }

      // Wait for any pending saves to complete (with timeout)
      if (saveManagerRef.current) {
        const hasActiveSaves = saveManagerRef.current.hasActiveSaves();
        logger.debug('Checking for active saves before auto-submit', { hasActiveSaves });
        
        if (hasActiveSaves) {
          logger.info('Waiting for active saves to complete before auto-submit...');
          const savesCompleted = await saveManagerRef.current.waitForSaves(AUTO_SUBMIT_SAVE_TIMEOUT_MS);
          if (!savesCompleted) {
            logger.warn('Some saves may not have completed before auto-submit');
          }
        }
      }
      
      // Submit the quiz
      logger.info('Submitting quiz after timer expiration');
      await submitWithRetry();
      logger.info('Auto-submit completed successfully');
    } catch (error) {
      logger.error('Auto-submit failed', { error });
      setError('Failed to submit quiz automatically. Please try submitting manually.');
    } finally {
      if (autoSubmitCountdownIntervalRef.current) {
        clearInterval(autoSubmitCountdownIntervalRef.current);
        autoSubmitCountdownIntervalRef.current = null;
      }

      if (isMountedRef.current) {
        setShowAutoSubmitCountdownModal(false);
        setAutoSubmitCountdown(AUTO_SUBMIT_MODAL_SECONDS);
        setIsAutoSubmitting(false);
      }
    }
  }, [attemptId, isAutoSubmitting, submitWithRetry, isMountedRef, saveManagerRef, AUTO_SUBMIT_SAVE_TIMEOUT_MS, AUTO_SUBMIT_MODAL_SECONDS, timerDeadlineMs, attempt]);
  
  // Use real-time countdown hook for accurate timer that doesn't pause during saves
  const countdownSeconds = useRealTimeCountdown({
    endAtMs: timerDeadlineMs,
    onEnd: handleTimerExpired
  });
  
  // Update timeRemaining state to match countdown (for backwards compatibility with existing UI)
  useEffect(() => {
    if (timerDeadlineMs !== null) {
      setTimeRemaining(countdownSeconds);
    }
  }, [countdownSeconds, timerDeadlineMs]);

  // OLD MANUAL TIMER LOGIC - REPLACED BY useRealTimeCountdown HOOK ABOVE
  // This old logic used setInterval and paused during saves, causing timer drift
  // The new hook uses absolute deadlines and never pauses

  // Prevent console access and source viewing
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      // Disable right-click context menu
      const handleContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      const handleKeyDown = (e) => {
        // F12
        if (e.keyCode === 123) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+I (DevTools)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+J (Console)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
          e.preventDefault();
          return false;
        }
        // Ctrl+U (View Source)
        if (e.ctrlKey && e.keyCode === 85) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+C (Element Inspector)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
          e.preventDefault();
          return false;
        }
        // Ctrl+S (Save Page)
        if (e.ctrlKey && e.keyCode === 83) {
          e.preventDefault();
          return false;
        }
        // Ctrl+A (Select All)
        if (e.ctrlKey && e.keyCode === 65) {
          e.preventDefault();
          return false;
        }
        // Ctrl+P (Print)
        if (e.ctrlKey && e.keyCode === 80) {
          e.preventDefault();
          return false;
        }
      };

      // Disable text selection
      const handleSelectStart = (e) => {
        e.preventDefault();
        return false;
      };

      // Disable drag
      const handleDragStart = (e) => {
        e.preventDefault();
        return false;
      };

      // Add event listeners
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('selectstart', handleSelectStart);
      document.addEventListener('dragstart', handleDragStart);

      // Disable console methods
      const disableConsole = () => {
        const noop = () => {};
        const methods = ['log', 'debug', 'info', 'warn', 'error', 'trace', 'dir', 'group', 'groupEnd', 'time', 'timeEnd', 'profile', 'profileEnd', 'count', 'clear', 'assert'];
        methods.forEach(method => {
          if (window.console && window.console[method]) {
            window.console[method] = noop;
          }
        });
      };

      disableConsole();

      // Cleanup
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('selectstart', handleSelectStart);
        document.removeEventListener('dragstart', handleDragStart);
      };
    }
  }, [timeRemaining]);

  // Auto-submit on browser close/refresh/navigation (timed quizzes only)
  useEffect(() => {
    // Only enable auto-submit for timed quizzes (timeRemaining must be set and > 0)
    // Untimed quizzes (even single-attempt) should NOT auto-submit on page unload
    const isTimedQuiz = timeRemaining !== null && timeRemaining > 0;
    const hasTimeLimit = attempt?.assignment?.time_limit_minutes && attempt.assignment.time_limit_minutes > 0;
    
    // Only proceed if this is actually a timed quiz
    if (!(isTimedQuiz && hasTimeLimit)) {
      return;
    }
    
    const handleBeforeUnload = (event) => {
      // CRITICAL FIX: Only auto-submit if timer has expired or is very close to expiring (within 5 seconds)
      // This prevents auto-submitting when user navigates away intentionally (e.g., clicking "Back to Quizzes")
      // Only auto-submit if timer has actually expired or is about to expire
      // Use current timeRemaining from state (closure will have latest value since effect re-runs on change)
      const currentTimeRemaining = timeRemaining;
      const shouldAutoSubmit = currentTimeRemaining !== null && currentTimeRemaining <= 5;
      
      if (!shouldAutoSubmit) {
        // Timer hasn't expired yet - just save, don't submit
        // This allows user to navigate away and come back without losing their attempt
        logger.debug('Timer not expired, saving progress only (not auto-submitting)', { 
          timeRemaining: currentTimeRemaining,
          attemptId 
        });
        
        // Still save responses, but don't submit
        const saveAllResponses = async () => {
          const token = localStorage.getItem('faithinsight_token');
          if (!token) {
            logger.warn('No auth token available for unload save');
            return;
          }

          const csrfToken = csrfTokenRef.current;
          if (!csrfToken) {
            logger.warn('CSRF token not available - skipping save to avoid 403 error');
            return;
          }

          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue;
            }
            
            let answerText = response.answer_text || '';
            if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
              answerText = JSON.stringify(response.selected_option_ids);
            }

            const data = JSON.stringify({
              question_id: parseInt(questionId),
              selected_option_id: response.selected_option_id,
              answer_text: answerText
            });

            const apiBaseURL = apiService.baseURL || '/portal/api';
            const url = `${window.location.origin}${apiBaseURL}/quizzes/student/attempts/${attemptId}/response`;
            
            const headers = {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-Token-Version': tokenRefreshManager.getTokenVersion().toString(),
              'X-CSRF-Token': csrfToken
            };
            
            fetch(url, {
              method: 'POST',
              headers,
              body: data,
              keepalive: true
            }).catch(err => {
              logger.warn('Error saving response during unload (non-blocking)', { error: err, questionId });
            });
          }
        };
        
        saveAllResponses();
        return; // Don't show warning or submit
      }
      
      // Timer has expired or is about to expire - auto-submit
      logger.info('Timer expired or close to expiring, auto-submitting on page unload', { 
        timeRemaining: currentTimeRemaining,
        attemptId 
      });
      
      // Save all responses before leaving using fetch with keepalive for reliability
      const saveAllResponses = async () => {
        const token = localStorage.getItem('faithinsight_token');
        if (!token) {
          logger.warn('No auth token available for unload save');
          return;
        }

        // CRITICAL FIX: Don't try to save if CSRF token is missing - it will fail with 403
        const csrfToken = csrfTokenRef.current;
        if (!csrfToken) {
          logger.warn('CSRF token not available - skipping save to avoid 403 error');
          // Don't attempt save without CSRF token - it will fail with 403
          // The save manager's periodic save will handle it when the page is active
          return;
        }

        // Check if token needs refresh before unload
        if (tokenRefreshManager.isRefreshTokenExpiringSoon()) {
          // Try to refresh token before saving (use keepalive)
          // Note: sendBeacon doesn't support auth, so we'll try to refresh synchronously if possible
          // For now, just use current token and hope it's still valid
          // Token refresh during unload is handled by the save manager's retry logic
        }

        // CRITICAL FIX: Only save responses with valid answers
        if (!quiz?.questions) return;
        
        for (const [questionId, response] of Object.entries(responses)) {
          const question = quiz.questions.find(q => q.id === questionId);
          if (!question || !response || !hasValidAnswer(question.type, response)) {
            continue; // Skip invalid or empty responses
          }
          
          // Ensure answer_text is properly set (same logic as handleSubmit)
          let answerText = response.answer_text || '';
          if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
            answerText = JSON.stringify(response.selected_option_ids);
          }

          // Use fetch with keepalive for reliable delivery during page unload
          const data = JSON.stringify({
            question_id: parseInt(questionId),
            selected_option_id: response.selected_option_id,
            answer_text: answerText
          });

          // CRITICAL FIX: Use correct API baseURL instead of hardcoded /api
          const apiBaseURL = apiService.baseURL || '/portal/api';
          const url = `${window.location.origin}${apiBaseURL}/quizzes/student/attempts/${attemptId}/response`;
          
          // Build headers with CSRF token (we've already checked it exists above)
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Token-Version': tokenRefreshManager.getTokenVersion().toString(),
            'X-CSRF-Token': csrfToken
          };
          
          // fetch with keepalive is more reliable than sendBeacon for authenticated requests
          fetch(url, {
            method: 'POST',
            headers,
            body: data,
            keepalive: true
          })
          .then(async (response) => {
            if (!response.ok) {
              // Log error details for debugging
              try {
                const errorData = await response.json().catch(() => ({}));
                logger.warn('HTTP error during unload save', { status: response.status, questionId, errorData });
              } catch (e) {
                logger.warn('HTTP error during unload save (could not parse error)', { status: response.status, questionId });
              }
            }
          })
          .catch(err => {
            // Silently handle errors - can't show UI feedback during unload
            logger.warn('Error saving response during unload (non-blocking)', { error: err, questionId });
          });
        }
      };

      // Submit the quiz using fetch with keepalive for reliability
      const submitQuiz = () => {
        const token = localStorage.getItem('faithinsight_token');
        if (!token) {
          logger.warn('No auth token available for unload submit');
          return;
        }

        // CRITICAL FIX: Don't try to submit if CSRF token is missing - it will fail with 403
        const csrfToken = csrfTokenRef.current;
        if (!csrfToken) {
          logger.warn('CSRF token not available - skipping submit to avoid 403 error');
          // Don't attempt submit without CSRF token - it will fail with 403
          // The user will need to submit manually when they return
          return;
        }

        try {
          // CRITICAL FIX: Use correct API baseURL instead of hardcoded /api
          const apiBaseURL = apiService.baseURL || '/portal/api';
          const endpoint = attempt && attempt.assignment && attempt.assignment.attempts_allowed === 1
            ? `${apiBaseURL}/quizzes/student/attempts/${attemptId}/auto-submit`
            : `${apiBaseURL}/quizzes/student/attempts/${attemptId}/submit`;

          // Build headers with CSRF token (we've already checked it exists above)
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfToken
          };

          // Use fetch with keepalive for reliable submission during unload
          fetch(`${window.location.origin}${endpoint}`, {
            method: 'POST',
            headers,
            keepalive: true
          })
          .then(async (response) => {
            if (!response.ok) {
              // Log error details for debugging
              try {
                const errorData = await response.json().catch(() => ({}));
                logger.warn('HTTP error during unload submit', { status: response.status, errorData });
              } catch (e) {
                logger.warn('HTTP error during unload submit (could not parse error)', { status: response.status });
              }
            }
          })
          .catch(err => {
            // Silently handle errors - can't show UI feedback during unload
            logger.warn('Error submitting quiz during unload (non-blocking)', { error: err });
          });
        } catch (err) {
          // Silently handle errors - can't show UI feedback during unload
          logger.warn('Error submitting quiz during unload (non-blocking)', { error: err });
        }
      };

      // Show warning message only if timer has expired
      event.preventDefault();
      event.returnValue = 'Your quiz will be automatically submitted if you leave this page. Are you sure you want to continue?';
      
      // CRITICAL FIX: Save and submit with CSRF tokens (synchronous - uses cached token)
      // The keepalive flag ensures requests complete even after page unloads
      saveAllResponses();
      submitQuiz();

      return event.returnValue;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden (tab switched, minimized, etc.)
        // Save responses but don't submit yet
        const saveAllResponses = async () => {
          // CRITICAL FIX: Only save responses with valid answers
          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue; // Skip invalid or empty responses
            }
            
            try {
              // Ensure answer_text is properly set (same logic as handleSubmit)
              let answerText = response.answer_text || '';
              if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
                answerText = JSON.stringify(response.selected_option_ids);
              }
              
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: answerText
              });
            } catch (err) {
              logger.error('Error saving response on visibility change', { error: err, questionId });
            }
          }
        };
        saveAllResponses();
      }
    };

      // Add event listeners
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    // If not a timed quiz, do nothing - untimed quizzes should not auto-submit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, responses, attemptId, attempt, quiz, hasValidAnswer]);

  // Save progress on browser close/refresh for untimed quizzes
  useEffect(() => {
    if (autoSaveEnabled && timeRemaining === null) {
      const handleBeforeUnload = (event) => {
        // Save all responses before leaving (don't submit)
        const saveAllResponses = async () => {
          // CRITICAL FIX: Only save responses with valid answers
          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue; // Skip invalid or empty responses
            }
            
            try {
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: response.answer_text
              });
            } catch (err) {
              logger.error('Error saving response on unload', { error: err, questionId });
            }
          }
        };

        // Show informational message
        event.preventDefault();
        event.returnValue = 'Your progress will be saved. You can resume this quiz later.';
        
        // Save responses in the background
        saveAllResponses();

        return event.returnValue;
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Page is hidden (tab switched, minimized, etc.)
          // Save responses
          const saveAllResponses = async () => {
            // CRITICAL FIX: Only save responses with valid answers
            if (!quiz?.questions) return;
            
            for (const [questionId, response] of Object.entries(responses)) {
              const question = quiz.questions.find(q => q.id === questionId);
              if (!question || !response || !hasValidAnswer(question.type, response)) {
                continue; // Skip invalid or empty responses
              }
              
              try {
                await quizApiService.saveResponse(attemptId, {
                  question_id: parseInt(questionId),
                  selected_option_id: response.selected_option_id,
                  answer_text: response.answer_text
                });
              } catch (err) {
                logger.error('Error saving response on visibility change', { error: err, questionId });
              }
            }
          };
          saveAllResponses();
        }
      };

      // Add event listeners
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [autoSaveEnabled, timeRemaining, responses, attemptId, quiz, hasValidAnswer]);

  const goToQuestion = useCallback((index) => {
    // Queue current question save without blocking normal navigation.
    queueCurrentQuestionSaveForNavigation();
    setCurrentQuestionIndex(index);
  }, [queueCurrentQuestionSaveForNavigation]);

  const enterReviewMode = useCallback(async () => {
    // CRITICAL FIX: Save current question before entering review mode
    // This prevents the last question from losing its answer
    try {
      await saveCurrentQuestionBeforeNavigation();
      
      // Wait a short time for save status to update (status updates are async)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // CRITICAL FIX: Clear ALL pending debounce timers and their 'saving' statuses
      // This prevents stuck 'Saving...' indicators on the review page
      const clearedQuestionIds = [];
      Object.keys(debounceTimersRef.current).forEach(questionId => {
        const timer = debounceTimersRef.current[questionId];
        if (timer) {
          clearTimeout(timer);
          delete debounceTimersRef.current[questionId];
          clearedQuestionIds.push(questionId);
        }
      });
      
      // Clear 'saving' status for ALL questions, not just the current one
      // This ensures the review page shows 'Saved' instead of 'Saving...'
      setSaveStatuses(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(questionId => {
          if (updated[questionId]?.status === 'saving') {
            // Change to 'saved' instead of deleting, so user sees it was saved
            updated[questionId] = {
              status: 'saved',
              timestamp: Date.now(),
              error: null
            };
          }
        });
        return updated;
      });
      
      logger.debug('Cleared all pending saves before entering review mode', { clearedQuestionIds });
    } catch (err) {
      // If save fails (e.g., 404 - attempt not found), log but don't block review mode
      // The save manager will have already updated the status to 'failed'
      logger.warn('Failed to save current question before entering review mode', { error: err });
      // Still allow entering review mode - user can see their answers
    }
    setEditingFromReview(false); // Clear the "editing from review" state
    setIsReviewMode(true);
  }, [saveCurrentQuestionBeforeNavigation]);

  const exitReviewMode = useCallback(async () => {
    // CRITICAL FIX: Save all answers before exiting review mode
    // This ensures any edits made in review mode are saved
    if (saveManagerRef.current && quiz?.questions) {
      // Clear all pending debounce timers
      Object.keys(debounceTimersRef.current).forEach(questionId => {
        const timer = debounceTimersRef.current[questionId];
        if (timer) {
          clearTimeout(timer);
          delete debounceTimersRef.current[questionId];
        }
      });
      
      // Get all responses (use both state and ref to capture latest)
      const allResponses = responsesRef.current || responses;
      
      // Save only currently visible/permitted quiz questions.
      // In partial retake/edit mode, responses may still include historical answers
      // for questions the student is not allowed to modify.
      const allowedQuestionIdSet = new Set((quiz.questions || []).map(q => String(q.id)));

      // Save all permitted responses that have valid answers
      const responsesToSave = {};
      for (const [questionId, response] of Object.entries(allResponses)) {
        if (!allowedQuestionIdSet.has(String(questionId))) {
          continue;
        }

        const question = quiz.questions.find(q => String(q.id) === String(questionId));
        if (question && response && hasValidAnswer(question.type, response)) {
          responsesToSave[questionId] = response;
        }
      }
      
      if (Object.keys(responsesToSave).length > 0) {
        try {
          const result = await saveManagerRef.current.saveAllResponses(responsesToSave, {
            skipVerification: true,
            maxRetries: 1
          });
          
          if (result.failed > 0) {
            logger.warn('Some answers failed to save before exiting review mode', { failed: result.failed });
            // Show warning but don't block exit - periodic save will retry
            // User can also manually save if needed
          } else {
            logger.debug('Saved all answers before exiting review mode');
          }
        } catch (err) {
          logger.warn('Error saving answers before exiting review mode', { error: err });
          // Show warning to user but don't block exit
          // Periodic save will catch it, or user can manually save
          if (err?.message) {
            logger.warn('Save error details', { error: err.message });
          }
        }
      }
    }
    
    setIsReviewMode(false);
  }, [quiz, responses, hasValidAnswer]);

  const goToQuestionFromReview = useCallback(async (questionIndex) => {
    // CRITICAL FIX: Save current question before navigating, even in review mode
    // If user edited an answer in review mode, we need to save it before navigating to another question
    if (currentQuestionIndex >= 0) {
      await saveCurrentQuestionBeforeNavigation();
    }
    
    // Clear all pending debounce timers to prevent stale "Saving..." states
    Object.keys(debounceTimersRef.current).forEach(questionId => {
      const timer = debounceTimersRef.current[questionId];
      if (timer) {
        clearTimeout(timer);
        delete debounceTimersRef.current[questionId];
      }
    });
    
    // Clear all save statuses to start fresh when editing from review
    setSaveStatuses({});
    
    comingFromReviewRef.current = true;
    setEditingFromReview(true); // Track that user came from review mode
    setIsReviewMode(false);
    setCurrentQuestionIndex(questionIndex);
  }, [currentQuestionIndex, saveCurrentQuestionBeforeNavigation]);

  // Scroll to question header when coming from review mode
  useEffect(() => {
    if (comingFromReviewRef.current && questionHeaderRef.current && !isReviewMode) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (questionHeaderRef.current) {
          const element = questionHeaderRef.current;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 20; // 20px offset from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          comingFromReviewRef.current = false;
        }
      }, 150);
    }
  }, [currentQuestionIndex, isReviewMode]);

  const isQuestionAnswered = (question, response) => {
    if (!response) return false;
    
    if (question.type === 'multiple_choice') {
      // Check if any options are selected (support both new and old format)
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return true;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      return response.selected_option_id !== null;
    } else if (question.type === 'true_false') {
      return response.selected_option_id !== null;
    } else if (question.type === 'matching') {
      // Check if matching pairs exist in answer_text
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            return true;
          }
        } catch (e) {
          // Not valid JSON
        }
      }
      return false;
    } else if (question.type === 'fill_blank') {
      const blankCount = getBlankCount(question.prompt);
      
      if (blankCount === 1) {
        // Single blank - check if answer_text exists and is not empty
        return response.answer_text && response.answer_text.trim() !== '';
      } else if (blankCount > 1) {
        // Multiple blanks - check if all blanks are filled
        if (!response.answer_texts || response.answer_texts.length !== blankCount) {
          return false;
        }
        // Check if all blanks have non-empty answers
        return response.answer_texts.every(answer => answer && answer.trim() !== '');
      }
      
      return false;
    }
    
    return false;
  };

  const isBlankFilled = (response, blankIndex) => {
    if (!response || !response.answer_texts) return false;
    return response.answer_texts[blankIndex] && response.answer_texts[blankIndex].trim() !== '';
  };

  const formatAnswerForReview = (question, response) => {
    if (!response) return '';
    
    if (question.type === 'multiple_choice') {
      // Get all selected option IDs (support both new and old format)
      let selectedOptionIds = [];
      
      // Try to parse from answer_text (new format for multiple selections)
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && response.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
      
      // Get all selected option texts
      const selectedOptions = selectedOptionIds
        .map(id => question.options.find(opt => opt.id === id))
        .filter(Boolean)
        .map(opt => opt.option_text);
      
      return selectedOptions.length > 0 ? selectedOptions.join(', ') : '';
    } else if (question.type === 'true_false') {
      const selectedOption = question.options.find(opt => opt.id === response.selected_option_id);
      return selectedOption ? selectedOption.option_text : '';
    } else if (question.type === 'fill_blank') {
      return response.answer_text || '';
    } else if (question.type === 'matching') {
      // Parse matching pairs from answer_text
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            const settings = question.settings || {};
            const leftItems = settings.leftItems || [];
            const rightItems = settings.rightItems || [];
            
            // Format pairs as "Term → Definition"
            return pairs.map(pair => {
              const left = leftItems[pair.leftIndex] || '';
              const right = rightItems[pair.rightIndex] || '';
              return `${left} → ${right}`;
            }).join('\n');
          }
        } catch (e) {
          // Not valid JSON, return empty
        }
      }
      return '';
    }
    
    return '';
  };

  const formatFillBlankPromptWithAnswers = (prompt, response) => {
    if (!prompt || !response || !response.answer_text) return prompt;
    
    // Split the answer text by pipe separator for multiple blanks
    const answers = response.answer_text.split('|');
    let formattedPrompt = prompt;
    let answerIndex = 0;
    
    // Replace each blank (underscores) with the corresponding answer
    formattedPrompt = formattedPrompt.replace(/_{1,}/g, (match) => {
      if (answerIndex < answers.length) {
        const answer = answers[answerIndex].trim();
        answerIndex++;
        return answer ? `[${answer}]` : match; // Show answer in brackets, or keep blank if empty
      }
      return match;
    });
    
    return formattedPrompt;
  };

  const renderQuestionOptions = (question, response) => {
    if (question.type !== 'multiple_choice' && question.type !== 'true_false') {
      return null;
    }

    // Get all selected option IDs for multiple choice (support both new and old format)
    let selectedOptionIds = [];
    if (question.type === 'multiple_choice') {
      // Try to parse from answer_text (new format for multiple selections)
      if (response?.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && response?.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
    } else if (question.type === 'true_false') {
      // True/false only has single selection
      if (response?.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
    }

    return (
      <div className={styles.reviewOptionsList}>
        {question.options.map(option => {
          const isSelected = selectedOptionIds.includes(option.id);
          return (
            <div 
              className={`${styles.reviewOption} ${isSelected ? styles.selected : ''}`}
              key={option.id}
            >
              {handleArabicText(option.option_text)}
            </div>
          );
        })}
      </div>
    );
  };

  const goToNext = useCallback(() => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      // Queue current question save without blocking normal navigation.
      queueCurrentQuestionSaveForNavigation();
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      
      // On mobile, scroll to question card immediately
      if (window.innerWidth <= 768) {
        // Small delay to ensure DOM is updated with new question
        setTimeout(() => {
          if (questionCardRef.current) {
            questionCardRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
          }
        }, 10);
      }
    }
  }, [currentQuestionIndex, quiz, queueCurrentQuestionSaveForNavigation]);

  const proceedToNextAfterSkipConfirmation = useCallback(() => {
    // The skip action depends on where the modal was opened from: advancing to the
    // next question (default) or entering review when on the last question.
    const proceed = skipConfirmation?.onProceed || goToNext;
    setSkipConfirmation(null);
    proceed();
  }, [skipConfirmation, goToNext]);

  const handleNextWithAnswerConfirmation = useCallback(() => {
    const questionForNavigation = quiz?.questions?.[currentQuestionIndex];
    if (!questionForNavigation) return;

    const response = responsesRef.current?.[questionForNavigation.id] || responses[questionForNavigation.id];
    const hasAnswer = hasValidAnswer(questionForNavigation.type, response);

    if (!hasAnswer) {
      setSkipConfirmation({
        title: 'Question Unanswered',
        message: "You haven’t selected an answer yet.\nWould you like to answer now or skip this question?",
        chooseLabel: 'Choose Answer',
        skipLabel: 'Skip & Continue'
      });
      return;
    }

    if (questionForNavigation.type === 'matching') {
      const matchingStatus = getMatchingCompletionStatus(questionForNavigation, response);
      if (matchingStatus.isPartiallyAnswered) {
        setSkipConfirmation({
          title: 'Incomplete matching answer',
          message: 'Some matching pairs are still unanswered.\nWould you like to choose the remaining matches or skip and come back later?',
          chooseLabel: 'Complete Answer',
          skipLabel: 'Skip & Continue'
        });
        return;
      }
    }

    goToNext();
  }, [quiz, currentQuestionIndex, responses, hasValidAnswer, getMatchingCompletionStatus, goToNext]);

  // Same "answer or skip" prompt as Next, but for the last question's "Review
  // Answers" button. Previously that button entered review directly, so an
  // unanswered last question skipped the confirmation entirely.
  const handleReviewWithAnswerConfirmation = useCallback(() => {
    const questionForNavigation = quiz?.questions?.[currentQuestionIndex];
    if (!questionForNavigation) {
      enterReviewMode();
      return;
    }

    const response = responsesRef.current?.[questionForNavigation.id] || responses[questionForNavigation.id];
    const hasAnswer = hasValidAnswer(questionForNavigation.type, response);

    if (!hasAnswer) {
      setSkipConfirmation({
        title: 'Question Unanswered',
        message: "You haven’t selected an answer yet.\nWould you like to answer now or skip to review?",
        chooseLabel: 'Choose Answer',
        skipLabel: 'Skip & Review',
        onProceed: enterReviewMode
      });
      return;
    }

    if (questionForNavigation.type === 'matching') {
      const matchingStatus = getMatchingCompletionStatus(questionForNavigation, response);
      if (matchingStatus.isPartiallyAnswered) {
        setSkipConfirmation({
          title: 'Incomplete matching answer',
          message: 'Some matching pairs are still unanswered.\nWould you like to choose the remaining matches or skip to review?',
          chooseLabel: 'Complete Answer',
          skipLabel: 'Skip & Review',
          onProceed: enterReviewMode
        });
        return;
      }
    }

    enterReviewMode();
  }, [quiz, currentQuestionIndex, responses, hasValidAnswer, getMatchingCompletionStatus, enterReviewMode]);

  const goToPrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      // Queue current question save without blocking normal navigation.
      queueCurrentQuestionSaveForNavigation();
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      
      // On mobile, scroll to question card immediately
      if (window.innerWidth <= 768) {
        // Small delay to ensure DOM is updated with new question
        setTimeout(() => {
          if (questionCardRef.current) {
            questionCardRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
          }
        }, 10);
      }
    }
  }, [currentQuestionIndex, queueCurrentQuestionSaveForNavigation]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <FiClock size={24} />
          <p>Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isAutoSubmitError = error.includes('auto-submit') || error.includes('Failed to auto-submit');
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>
            {isAutoSubmitted ? '✅' : isAutoSubmitError ? '⚠️' : '⚠️'}
          </div>
          <h2 className={styles.errorTitle}>
            {isAutoSubmitted ? 'Quiz Auto-Submitted' : isAutoSubmitError ? 'Auto-Submit Failed' : 'Error Loading Quiz'}
          </h2>
          <p className={styles.errorMessage}>
            {error}
          </p>
          {isAutoSubmitted ? (
            <button className={styles.errorAction} onClick={() => navigate(`/student/result/${attemptId}`)}>
              <FiCheckCircle size={16} />
              View Results
            </button>
          ) : isAutoSubmitError ? (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className={styles.errorAction} onClick={() => handleSubmit(false)} disabled={saving}>
                <FiCheckCircle size={16} />
                Submit Quiz Manually
              </button>
              <button className={styles.errorAction} onClick={() => navigate('/student/quizzes')} style={{ background: 'var(--text-muted)' }}>
                <FiArrowLeft size={16} />
                Back to Quizzes
              </button>
            </div>
          ) : (
            <button className={styles.errorAction} onClick={() => navigate('/student/quizzes')}>
              <FiArrowLeft size={16} />
              Back to Quizzes
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!quiz || !attempt) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>❌</div>
          <h2 className={styles.errorTitle}>Quiz Not Found</h2>
          <p className={styles.errorMessage}>
            The quiz you're looking for could not be found or you don't have permission to access it.
          </p>
          <button className={styles.errorAction} onClick={() => navigate('/student/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </button>
        </div>
      </div>
    );
  }

  // CRITICAL FIX: Validate currentQuestionIndex is within bounds
  // This is the final safety check before rendering - catches any edge cases
  if (currentQuestionIndex < 0 || currentQuestionIndex >= quiz.questions.length) {
    logger.error('currentQuestionIndex out of bounds at render time - final safety check', { 
      currentQuestionIndex, 
      questionsLength: quiz.questions.length,
      attemptId,
      scenario: 'Student was on question #' + (currentQuestionIndex + 1) + ' but quiz only has ' + quiz.questions.length + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set, or the quiz was filtered after the student navigated to that question.'
    });
    // Reset to first question
    if (quiz.questions.length > 0) {
      setCurrentQuestionIndex(0);
      return (
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <FiClock size={24} />
            <p>Loading question...</p>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.container}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>❌</div>
            <h2 className={styles.errorTitle}>Quiz Has No Questions</h2>
            <p className={styles.errorMessage}>
              This quiz has no questions available. Please contact your instructor.
            </p>
            <button className={styles.errorAction} onClick={() => navigate('/student/quizzes')}>
              <FiArrowLeft size={16} />
              Back to Quizzes
            </button>
          </div>
        </div>
      );
    }
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  
  // CRITICAL FIX: Double-check currentQuestion exists
  // Use a ref to prevent rapid resets that could cause flickering
  if (!currentQuestion) {
    const now = Date.now();
    // Only reset if we haven't reset in the last 500ms (prevents flickering)
    if (now - lastIndexResetRef.current > 500) {
      logger.error('currentQuestion is undefined at render time - this should not happen if validation worked', { 
        currentQuestionIndex, 
        questionsLength: quiz.questions.length,
        attemptId,
        scenario: 'Question at index ' + currentQuestionIndex + ' is undefined even though index is within bounds. This indicates a data inconsistency.'
      });
      // Reset to first question
      if (quiz.questions.length > 0) {
        lastIndexResetRef.current = now;
        setCurrentQuestionIndex(0);
        return (
          <div className={styles.container}>
            <div className={styles.loadingState}>
              <FiClock size={24} />
              <p>Loading question...</p>
            </div>
          </div>
        );
      } else {
        return (
          <div className={styles.container}>
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>❌</div>
              <h2 className={styles.errorTitle}>Question Not Found</h2>
              <p className={styles.errorMessage}>
                The question you're trying to access could not be found. Please try refreshing the page.
              </p>
              <button className={styles.errorAction} onClick={() => navigate('/student/quizzes')}>
                <FiArrowLeft size={16} />
                Back to Quizzes
              </button>
            </div>
          </div>
        );
      }
    } else {
      // If we recently reset, just show loading to prevent flickering
      return (
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <FiClock size={24} />
            <p>Loading question...</p>
          </div>
        </div>
      );
    }
  }
  
  const currentResponse = responses[currentQuestion.id];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  const getTimerProps = () => {
    if (!timeRemaining) return {};
    if (timeRemaining <= 60) return { danger: true };
    if (timeRemaining <= 300) return { warning: true };
    return {};
  };

  return (
    <div className={styles.quizPlayerRoot}>
      {/* Network Error Banner */}
      {networkErrorBanner && (
        <div className={`${styles.networkErrorBanner} ${networkErrorBanner.severity === 'critical' ? styles.critical : networkErrorBanner.severity === 'warning' ? styles.warning : ''}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            {networkErrorBanner.severity === 'critical' && <FiAlertCircle size={20} />}
            {networkErrorBanner.severity === 'warning' && <FiAlertCircle size={20} />}
            {networkErrorBanner.severity === 'info' && <FiCheckCircle size={20} />}
            <span>
              {networkErrorBanner.message}
              {networkErrorBanner.count > 0 && ` (${networkErrorBanner.count} answer${networkErrorBanner.count !== 1 ? 's' : ''})`}
            </span>
          </div>
          {networkErrorBanner.type !== 'restored' && (
            <button
              style={{ background: 'transparent', border: '1px solid currentColor', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', color: 'inherit', fontSize: '13px' }}
              onClick={() => setNetworkErrorBanner(null)}
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {firewallWarning && (
        <div className={`${styles.networkErrorBanner} ${styles.warning}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <FiInfo size={20} />
            <span>{firewallWarning.message}</span>
          </div>
          <button
            style={{ background: 'transparent', border: '1px solid currentColor', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', color: 'inherit', fontSize: '13px' }}
            onClick={() => setFirewallWarning(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Auto-Submit Status Banner */}
      {isAutoSubmitting && !showAutoSubmitCountdownModal && timeRemaining === 0 && (
        <div className={styles.networkErrorBanner} style={{ backgroundColor: 'var(--info-color)', color: 'white', borderColor: 'var(--info-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <FiRefreshCw size={20} className={styles.spinner} />
            <span>
              <strong>Auto-Submitting Quiz...</strong> Please wait while your quiz is being submitted automatically. Do not close this page.
            </span>
          </div>
        </div>
      )}
      
      {/* Auto-Submit Overlay */}
      {isAutoSubmitting && !showAutoSubmitCountdownModal && (
        <div className={styles.autoSubmitOverlay}>
          {error && (error.includes('auto-submit') || error.includes('taking too long') || error.includes('Submission')) ? (
            <>
              <FiAlertCircle size={48} style={{ color: 'white' }} />
              <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>Submission Failed</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '500px', textAlign: 'center', fontSize: '14px' }}>
                {error}
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                <button
                  onClick={() => {
                    setIsAutoSubmitting(false);
                    setError(null);
                    handleSubmit(false);
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: 'var(--error-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Submit Manually
                </button>
                <button
                  onClick={() => {
                    setIsAutoSubmitting(false);
                    setError(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '2px solid white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <>
              <FiRefreshCw size={48} className={styles.spinner} style={{ color: 'white' }} />
              <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>Submitting Quiz...</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>Please wait, do not close this page</div>
            </>
          )}
        </div>
      )}

      {/* Timed single-attempt expiry warning modal */}
      {showAutoSubmitCountdownModal && (
        <div className={styles.autoSubmitOverlay}>
          <FiClock size={48} style={{ color: 'white' }} />
          <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>Quiz Timer Has Expired</div>
          <div style={{ color: 'rgba(255,255,255,0.95)', maxWidth: '560px', textAlign: 'center', fontSize: '14px', lineHeight: 1.5 }}>
            In <strong>{autoSubmitCountdown}</strong> second{autoSubmitCountdown !== 1 ? 's' : ''}, all answers will be submitted automatically.
            Please keep this page open while we save your latest answers.
          </div>
        </div>
      )}
      
      {/* Re-Authentication Modal for Untimed Quizzes */}
      <QuizReAuthModal
        open={showReAuthModal}
        onSuccess={() => {
          logger.info('Re-authentication successful, resuming quiz');
          setShowReAuthModal(false);
          // Process any failed saves after re-authentication
          if (saveManagerRef.current) {
            saveManagerRef.current.processFailedQueue();
          }
        }}
        onCancel={() => {
          logger.info('User chose to exit quiz for re-authentication later');
          setShowReAuthModal(false);
          // Navigate to quiz list - answers are saved locally
          navigate('/student/quizzes', {
            state: {
              message: 'Your answers have been saved locally. Please log in again to continue your quiz.',
              attemptId: attemptId
            }
          });
        }}
        attemptId={attemptId}
        quizTitle={quiz?.title}
        savedAnswersCount={reAuthSavedCount}
      />

      {skipConfirmation && (
        <div
          className={styles.confirmationOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-confirmation-title"
          onClick={() => setSkipConfirmation(null)}
        >
          <div className={styles.confirmationDialog} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={styles.confirmationCloseButton}
              onClick={() => setSkipConfirmation(null)}
              aria-label="Close confirmation"
            >
              <FiX size={18} />
            </button>
            <h2 id="skip-confirmation-title" className={styles.confirmationTitle}>{skipConfirmation.title}</h2>
            <p className={styles.confirmationMessage}>{skipConfirmation.message}</p>
            <div className={styles.confirmationActions}>
              <button
                type="button"
                className={`${styles.confirmationButton} ${styles.primary}`}
                onClick={() => setSkipConfirmation(null)}
              >
                {skipConfirmation.chooseLabel}
              </button>
              <button
                type="button"
                className={`${styles.confirmationButton} ${styles.secondary}`}
                onClick={proceedToNextAfterSkipConfirmation}
              >
                {skipConfirmation.skipLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {isReviewMode ? (
        // Review Mode
        <div className={styles.reviewContainer}>
          <div className={styles.reviewHeader}>
            {timeRemaining !== null && (
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div className={`${styles.timer} ${timeRemaining <= 60 ? styles.danger : timeRemaining <= 300 ? styles.warning : ''}`}>
                  <FiClock size={16} />
                  Quiz Timer: {timeRemaining === 0 ? (
                    <span className={styles.timerExpiredText}>Time Expired - Submitting...</span>
                  ) : (
                    quizApiService.formatTimeRemaining(timeRemaining)
                  )}
                </div>
              </div>
            )}
            <h1 className={styles.reviewTitle}>Review Your Answers</h1>
            <p className={styles.reviewSubtitle}>
              Please review all your answers before submitting. You can go back to edit any question.
            </p>
          </div>
          
          {quiz.questions.map((question, index) => {
            const response = responses[question.id];
            const answerText = formatAnswerForReview(question, response);
            const isAnswered = isQuestionAnswered(question, response);
            
            return (
              <div className={`${styles.reviewQuestion} ${!isAnswered ? styles.unanswered : ''}`} key={question.id}>
                <div className={styles.reviewQuestionHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {question.extra_credit && (
                      <span className={styles.extraCreditBadge}>Extra Credit</span>
                    )}
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {parseFloat(question.points || 1).toFixed(2)} {parseFloat(question.points || 1) === 1 ? 'pt' : 'pts'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {saveStatuses[question.id] && (
                      <span className={`${styles.statusPill} ${styles[saveStatuses[question.id].status]}`}>
                        {saveStatuses[question.id].status === 'saving' && '⏳ Saving...'}
                        {saveStatuses[question.id].status === 'saved' && '✓ Saved'}
                        {saveStatuses[question.id].status === 'failed' && '⚠ Failed'}
                      </span>
                    )}
                    <button className={styles.editLink} onClick={() => goToQuestionFromReview(index)}>
                      Edit Answer
                    </button>
                  </div>
                </div>
                
                {question.type === 'fill_blank' && response && response.answer_text ? (
                  <div className={styles.reviewQuestionPromptWithHTML}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlForViewer(`${index + 1}. ${formatFillBlankPromptWithAnswers(handleArabicText(question.prompt), response)
                        .replace(/\[([^\]]+)\]/g, '<span class="fill-blank-answer">$1</span>')}`)
                    }}
                  />
                ) : (
                  <div className={styles.reviewQuestionPrompt}>
                    {index + 1}. {handleArabicText(question.prompt)}
                  </div>
                )}
                
                <div className={styles.reviewAnswer}>
                  {(question.type === 'multiple_choice' || question.type === 'true_false') ? (
                    <>
                      {renderQuestionOptions(question, response)}
                      {!isAnswered && (
                        <div className={styles.reviewAnswerEmpty}>No option selected</div>
                      )}
                    </>
                  ) : question.type === 'fill_blank' ? (
                    !isAnswered && (
                      <div className={styles.reviewAnswerEmpty}>
                        {(() => {
                          const blankCount = getBlankCount(question.prompt);
                          if (blankCount > 1) {
                            const emptyBlanks = [];
                            for (let i = 0; i < blankCount; i++) {
                              if (!isBlankFilled(response, i)) {
                                emptyBlanks.push(i + 1);
                              }
                            }
                            return emptyBlanks.length > 0
                              ? `Enter a response for Blank Answer ${emptyBlanks.join(', Blank Answer ')}`
                              : 'No answer provided';
                          }
                          return 'No answer provided';
                        })()}
                      </div>
                    )
                  ) : question.type === 'matching' ? (
                    isAnswered && answerText ? (
                      <div className={styles.reviewAnswerText}>
                        {answerText.split('\n').map((line, i) => (
                          <div key={i} style={{ marginBottom: '8px' }}>{line}</div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.reviewAnswerEmpty}>No answer provided</div>
                    )
                  ) : (
                    answerText ? (
                      <div className={styles.reviewAnswerText}>
                        {answerText.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.reviewAnswerEmpty}>No answer provided</div>
                    )
                  )}
                </div>
              </div>
            );
          })}
          
          <div className={styles.reviewActions}>
            <button className={`${styles.reviewButton} ${styles.secondary}`} onClick={exitReviewMode}>
              <FiArrowLeft size={16} />
              Back to Quiz
            </button>
            <button
              className={`${styles.reviewButton} ${styles.success}`}
              onClick={() => handleSubmit(false)}
              disabled={saving || hasUnsavedAnswers || isAutoSubmitting}
              title={
                isAutoSubmitting ? 'Auto-submitting...'
                  : hasUnsavedAnswers ? `Saving ${unsavedCount} answer(s)...`
                  : saving ? 'Submitting quiz...'
                  : 'Submit quiz'
              }
            >
              <FiCheckCircle size={16} />
              {saving
                ? 'Submitting...'
                : hasUnsavedAnswers
                  ? (unsavedCount > 0
                      ? `Saving ${unsavedCount} answer(s)...`
                      : 'Saving answers...')
                  : 'Submit Quiz'}
            </button>
          </div>
        </div>
      ) : (
        // Quiz Mode - Main content
        <>
          <div className={styles.container}>
            {/* Header Card */}
            <div className={styles.header}>
              <div className={styles.headerTop}>
                <div className={styles.quizInfo}>
                  <h1 className={styles.quizTitle}>{quiz.title}</h1>
                  {quiz.description && <p className={styles.quizDescription}>{quiz.description}</p>}
                </div>
                
              </div>
              
              {/* Metadata Grid */}
              <div className={styles.metadataGrid}>
                {/* Row 1: Course | Instructor | Questions */}
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <FiBookOpen size={12} />
                    Course
                  </span>
                  <span className={styles.metadataValue}>
                    {attempt?.assignment?.course_name || quiz?.course_name || attempt?.course_name || 'N/A'}
                  </span>
                </div>
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <FiUser size={12} />
                    Instructor
                  </span>
                  <span className={styles.metadataValue}>
                    {getInstructorName(attempt?.assignment) || getInstructorName(quiz) || getInstructorName(attempt) || 'N/A'}
                  </span>
                </div>
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <FiFileText size={12} />
                    Questions
                  </span>
                  <span className={styles.metadataValue}>{quiz.questions.length}</span>
                </div>
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <FiAward size={12} />
                    Total Points
                  </span>
                  <span className={styles.metadataValue}>
                    {quiz.questions.reduce((total, q) => total + parseFloat(q.points || 1), 0)}
                  </span>
                </div>
                
                {/* Row 2: Due Date | Portal Status | Total Points */}
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <FiCalendar size={12} />
                    Due Date
                  </span>
                  <span className={styles.metadataValue}>{dueDate ? formatDueDate(dueDate) : 'N/A'}</span>
                </div>
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    {networkStatus === 'online' ? <FiCheckCircle size={12} /> : <FiAlertCircle size={12} />}
                    Portal Status
                  </span>
                  <span className={`${styles.metadataValue} ${styles[`status${networkStatus.charAt(0).toUpperCase() + networkStatus.slice(1)}`]}`}>
                    {networkStatus === 'offline' && 'Offline'}
                    {networkStatus === 'unstable' && 'Unstable'}
                    {networkStatus === 'online' && 'Online'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Question Card - Main interactive area */}
            <div className={styles.questionCard} ref={questionCardRef}>
              <div className={styles.questionHeader}>
                <div className={styles.questionMeta}>
                  <span className={styles.questionLabel}>
                    <span className={styles.questionNumberPill}>Question {currentQuestionIndex + 1}</span>
                    <span className={styles.questionTypePointsLine}>
                      <span className={styles.questionTypeLabel}>{formatQuestionTypeLabel(currentQuestion.type)}</span>
                      <span className={styles.questionPointsText}>
                        ({parseFloat(currentQuestion.points || 1).toFixed(2)} {parseFloat(currentQuestion.points || 1) === 1 ? 'PT' : 'PTS'})
                      </span>
                    </span>
                  </span>
                  {currentQuestion.extra_credit && (
                    <span className={styles.extraCreditBadge}>Extra Credit</span>
                  )}
                </div>
                <div className={styles.questionHeaderBadges}>
                  {timeRemaining !== null && (
                    <div className={`${styles.timerBadge} ${timeRemaining <= 60 ? styles.danger : timeRemaining <= 300 ? styles.warning : ''}`}>
                      <FiClock size={16} />
                      {timeRemaining === 0 ? (
                        <span className={styles.timerExpiredText}>Time Expired</span>
                      ) : (
                        quizApiService.formatTimeRemaining(timeRemaining)
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <h2 className={styles.questionPrompt}>
                {currentQuestion.type === 'fill_blank'
                  ? formatFillBlankPrompt(handleArabicText(stripQuestionPromptPrefix(currentQuestion.prompt)))
                  : handleArabicText(stripQuestionPromptPrefix(currentQuestion.prompt))}
              </h2>

            {currentQuestion.type === 'multiple_choice' && (
              <div className={styles.answerOptions}>
                {/* Show instruction if there are multiple correct answers */}
                {currentQuestion.options.filter(opt => opt.is_correct).length > 1 && (
                  <p className={styles.optionInstruction}>
                    You can select multiple answers. Check all that apply.
                  </p>
                )}
                {currentQuestion.options.map(option => {
                  // Check if this option is selected (support both old and new format)
                  let isSelected = false;
                  
                  // Try to parse from answer_text (new format for multiple selections)
                  if (currentResponse?.answer_text) {
                    try {
                      const parsed = JSON.parse(currentResponse.answer_text);
                      if (Array.isArray(parsed)) {
                        isSelected = parsed.includes(option.id);
                      }
                    } catch (e) {
                      // Not JSON, fall through to legacy check
                    }
                  }
                  
                  // Fallback to selected_option_id (legacy single selection)
                  if (!isSelected) {
                    isSelected = currentResponse?.selected_option_id === option.id;
                  }
                  
                  return (
                    <label 
                      key={option.id}
                      className={`${styles.answerOption} ${isSelected ? styles.selected : ''}`}
                    >
                      <input 
                        className={styles.optionInput}
                        type="checkbox"
                        checked={isSelected}
                        disabled={isAutoSubmitting}
                        onChange={() => handleResponseChange(currentQuestion.id, option.id, 'multiple_choice')}
                      />
                      <span className={styles.answerText}>{handleArabicText(option.option_text)}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {currentQuestion.type === 'true_false' && (
              <div className={styles.answerOptions}>
                {currentQuestion.options.map(option => {
                  return (
                    <label 
                      key={option.id}
                      className={`${styles.answerOption} ${currentResponse?.selected_option_id === option.id ? styles.selected : ''}`}
                    >
                      <input 
                        className={styles.optionInput}
                        type="radio"
                        name={`question_${currentQuestion.id}`}
                        checked={currentResponse?.selected_option_id === option.id}
                        disabled={isAutoSubmitting}
                        onChange={() => handleResponseChange(currentQuestion.id, option.id, 'true_false')}
                      />
                      <span className={styles.answerText}>{handleArabicText(option.option_text)}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {currentQuestion.type === 'fill_blank' && (() => {
              const blankCount = getBlankCount(currentQuestion.prompt);
              
              if (blankCount === 1) {
                // Single blank - use the original single input
                return (
                  <input className={styles.fillBlankInput}
                    type="text"
                    value={currentResponse?.answer_text || ''}
                    disabled={isAutoSubmitting}
                    onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, 'fill_blank')}
                    placeholder="Enter your answer..."
                  />
                );
              } else {
                // Multiple blanks - show separate inputs for each
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {Array.from({ length: blankCount }, (_, index) => {
                      return (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '500', 
                            color: 'var(--text-primary)'
                          }}>
                            Blank Answer {index + 1}:
                          </label>
                          <input className={styles.fillBlankInput}
                            type="text"
                            value={currentResponse?.answer_texts?.[index] || ''}
                            disabled={isAutoSubmitting}
                            onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, 'fill_blank', index)}
                            placeholder={`Enter answer for blank ${index + 1}...`}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              }
            })()}

            {currentQuestion.type === 'matching' && (() => {
              const settings = currentQuestion.settings || {};
              const leftItems = settings.leftItems || [];
              const rightItems = settings.rightItems || [];
              
              // Shuffle arrays for display (deterministic based on question ID)
              const shuffleWithMapping = (array, seed) => {
                // Filter out empty items and create mapping
                const validItems = [];
                const originalToValid = {}; // originalIndex -> validIndex
                array.forEach((item, originalIndex) => {
                  if (item && item.trim()) {
                    originalToValid[originalIndex] = validItems.length;
                    validItems.push({ item, originalIndex });
                  }
                });
                
                if (validItems.length === 0) {
                  return { shuffledArray: [], shuffleMapping: {}, reverseMapping: {} };
                }
                
                // Create array of valid indices to shuffle
                const validIndices = validItems.map((_, i) => i);
                const shuffledValidIndices = [...validIndices];
                
                // Use a simple seeded random number generator
                let seedValue = seed;
                const seededRandom = () => {
                  seedValue = (seedValue * 9301 + 49297) % 233280;
                  return seedValue / 233280;
                };
                
                // Fisher-Yates shuffle with seeded random
                for (let i = shuffledValidIndices.length - 1; i > 0; i--) {
                  const j = Math.floor(seededRandom() * (i + 1));
                  [shuffledValidIndices[i], shuffledValidIndices[j]] = [shuffledValidIndices[j], shuffledValidIndices[i]];
                }
                
                // Create shuffled array and mapping
                const shuffledArray = shuffledValidIndices.map(validIndex => validItems[validIndex].item);
                const shuffleMapping = {}; // shuffledIndex -> originalIndex
                const reverseMapping = {}; // originalIndex -> shuffledIndex
                
                shuffledValidIndices.forEach((validIndex, shuffledIndex) => {
                  const originalIndex = validItems[validIndex].originalIndex;
                  shuffleMapping[shuffledIndex] = originalIndex;
                  reverseMapping[originalIndex] = shuffledIndex;
                });
                
                return { shuffledArray, shuffleMapping, reverseMapping };
              };
              
              // Use question ID as seed for deterministic shuffling
              const seed = currentQuestion.id || 0;
              const leftShuffle = shuffleWithMapping(leftItems, seed);
              const rightShuffle = shuffleWithMapping(rightItems, seed + 1000); // Different seed for right items
              
              const shuffledLeftItems = leftShuffle.shuffledArray;
              const shuffledRightItems = rightShuffle.shuffledArray;
              const leftShuffleMapping = leftShuffle.shuffleMapping;
              const rightShuffleMapping = rightShuffle.shuffleMapping;
              const rightReverseMapping = rightShuffle.reverseMapping;
              
              // Parse current response pairs (using original indices)
              let currentPairs = [];
              try {
                if (currentResponse?.answer_text) {
                  currentPairs = JSON.parse(currentResponse.answer_text);
                }
              } catch (e) {
                currentPairs = [];
              }
              
              // Convert original indices to shuffled indices for display
              const getShuffledRightIndexForShuffledLeft = (shuffledLeftIndex) => {
                const originalLeftIndex = leftShuffleMapping[shuffledLeftIndex];
                const pair = currentPairs.find(p => p.leftIndex === originalLeftIndex);
                if (pair) {
                  // Convert original rightIndex to shuffled rightIndex
                  return rightReverseMapping[pair.rightIndex];
                }
                return null;
              };
              
              return (
                <div style={{ marginTop: '1rem' }}>
                  <div className={styles.matchingHeaderGrid}>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: 'var(--text-primary)',
                        lineHeight: '1.4'
                      }}>
                        Terms
                      </h4>
                    </div>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: 'var(--text-primary)',
                        lineHeight: '1.4'
                      }}>
                        Definitions
                      </h4>
                    </div>
                  </div>
                  
                  {shuffledLeftItems.map((leftItem, shuffledLeftIndex) => {
                    if (!leftItem || !leftItem.trim()) return null;
                    
                    // Get original leftIndex from shuffled index
                    const originalLeftIndex = leftShuffleMapping[shuffledLeftIndex];
                    const selectedShuffledRightIndex = getShuffledRightIndexForShuffledLeft(shuffledLeftIndex);
                    
                    return (
                      <div className={styles.matchingItemGrid} key={shuffledLeftIndex}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          fontWeight: '500',
                          color: 'var(--text-primary)',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          minWidth: 0, // Allows flex item to shrink below content size
                          flex: '1 1 0%' // Flexible sizing
                        }}>
                          {leftItem}
                        </div>
                        <div className={styles.customDropdown} 
                          data-dropdown
                          ref={el => {
                            const dropdownKey = `${currentQuestion.id}-${shuffledLeftIndex}`;
                            dropdownRefs.current[dropdownKey] = el;
                          }}
                        >
                          <button className={styles.dropdownButton}
                            type="button"
                            hasValue={selectedShuffledRightIndex !== null}
                            disabled={isAutoSubmitting}
                            onClick={() => {
                              if (isAutoSubmitting) return; // UI FIX: Prevent interaction during auto-submit
                              const dropdownKey = `${currentQuestion.id}-${shuffledLeftIndex}`;
                              const button = dropdownRefs.current[dropdownKey]?.querySelector('button');
                              if (button) {
                                const rect = button.getBoundingClientRect();
                                const isMobile = window.innerWidth <= 768;
                                const isSmallMobile = window.innerWidth <= 480;
                                setDropdownPositions(prev => ({
                                  ...prev,
                                  [dropdownKey]: {
                                    top: Math.min(rect.bottom + 4, window.innerHeight - 80),
                                    left: isMobile ? Math.max(8, rect.left) : rect.left,
                                    width: isMobile 
                                      ? (isSmallMobile 
                                          ? Math.min(rect.width, window.innerWidth - 16)
                                          : Math.min(rect.width, window.innerWidth - 24))
                                      : rect.width
                                  }
                                }));
                              }
                              setOpenDropdowns(prev => ({
                                ...prev,
                                [dropdownKey]: !prev[dropdownKey]
                              }));
                            }}
                          >
                            {selectedShuffledRightIndex !== null 
                              ? (shuffledRightItems[selectedShuffledRightIndex] || `Definition ${selectedShuffledRightIndex + 1}`)
                              : 'Select a match...'}
                            <span className={styles.dropdownIcon}>
                              {openDropdowns[`${currentQuestion.id}-${shuffledLeftIndex}`] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                            </span>
                          </button>
                          {openDropdowns[`${currentQuestion.id}-${shuffledLeftIndex}`] && createPortal(
                            <div className={styles.dropdownMenu} 
                              data-dropdown
                              style={dropdownPositions[`${currentQuestion.id}-${shuffledLeftIndex}`]}
                            >
                              <button className={styles.dropdownOption}
                                type="button"
                                onClick={() => {
                                  // Convert shuffled indices back to original indices
                                  handleResponseChange(
                                    currentQuestion.id, 
                                    { leftIndex: originalLeftIndex, rightIndex: null }, 
                                    'matching'
                                  );
                                  setOpenDropdowns(prev => {
                                    const newState = { ...prev };
                                    delete newState[`${currentQuestion.id}-${shuffledLeftIndex}`];
                                    return newState;
                                  });
                                }}
                              >
                                {selectedShuffledRightIndex !== null ? 'Clear selection' : 'Select a match...'}
                              </button>
                              {shuffledRightItems.map((rightItem, shuffledRightIndex) => {
                                if (!rightItem || !rightItem.trim()) return null;
                                // Get original rightIndex from shuffled index
                                const originalRightIndex = rightShuffleMapping[shuffledRightIndex];
                                return (
                                  <button className={`${styles.dropdownOption} ${selectedShuffledRightIndex === shuffledRightIndex ? 'selected' : ''}`}
                                    key={shuffledRightIndex}
                                    type="button"
                                    onClick={() => {
                                      // Convert shuffled indices back to original indices for saving
                                      handleResponseChange(
                                        currentQuestion.id, 
                                        { leftIndex: originalLeftIndex, rightIndex: originalRightIndex }, 
                                        'matching'
                                      );
                                      setOpenDropdowns(prev => {
                                        const newState = { ...prev };
                                        delete newState[`${currentQuestion.id}-${shuffledLeftIndex}`];
                                        return newState;
                                      });
                                    }}
                                  >
                                    {rightItem || `Definition ${shuffledRightIndex + 1}`}
                                  </button>
                                );
                              })}
                            </div>,
                            document.body
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <div className={styles.questionActions}>
              {/* Save status indicator - autosave handles saving automatically */}
              <span className={`${styles.saveStatusInline} ${saveStatuses[currentQuestion?.id] ? styles[saveStatuses[currentQuestion.id].status] : styles.empty}`}>
                {saveStatuses[currentQuestion?.id] && (
                  <>
                  {saveStatuses[currentQuestion.id].status === 'saving' && (
                    <>
                      <FiClock size={12} />
                      Saving...
                    </>
                  )}
                  {saveStatuses[currentQuestion.id].status === 'saved' && (
                    <>
                      <FiCheckCircle size={12} />
                      Saved
                    </>
                  )}
                  {saveStatuses[currentQuestion.id].status === 'failed' && (
                    <>
                      <FiAlertCircle size={12} />
                      Failed - <button 
                        className={styles.retryLink}
                        onClick={() => saveResponse(currentQuestion.id)}
                        disabled={saving}
                      >
                        Retry
                      </button>
                    </>
                  )}
                  </>
                )}
              </span>
            </div>
          </div>
          
            {/* Navigation - Below question card */}
            <div className={styles.navigation}>
              {/* Progress indicator - shown above buttons on mobile */}
              <div className={styles.progressIndicator}>
                <span className={styles.progressText}>
                  {currentQuestionIndex + 1} / {quiz.questions.length}
                </span>
                <div className={styles.progressBarSmall}>
                  <div className={styles.progressFillSmall} style={{ width: `${progress}%` }} />
                </div>
              </div>

              {/* Navigation buttons container */}
              <div className={styles.navButtonsRow}>
                <button
                  className={`${styles.navButton} ${styles.secondary}`}
                  onClick={goToPrevious}
                  disabled={currentQuestionIndex === 0}
                >
                  <FiArrowLeft size={16} />
                  Previous
                </button>

                {/* Show "Back to Review" button when user came from review mode */}
                {editingFromReview ? (
                  <button
                    className={`${styles.navButton} ${styles.primary}`}
                    onClick={enterReviewMode}
                    disabled={saving || hasUnsavedAnswers || isAutoSubmitting}
                    title={hasUnsavedAnswers ? `Saving ${unsavedCount} answer(s)...` : 'Return to review page'}
                  >
                    <FiCheckCircle size={16} />
                    {hasUnsavedAnswers
                      ? (unsavedCount > 0 ? `Saving ${unsavedCount}...` : 'Saving...')
                      : 'Back to Review'}
                  </button>
                ) : currentQuestionIndex === quiz.questions.length - 1 ? (
                  <button
                    className={`${styles.navButton} ${styles.primary}`}
                    onClick={handleReviewWithAnswerConfirmation}
                    disabled={saving || hasUnsavedAnswers || isAutoSubmitting}
                    title={
                      isAutoSubmitting ? 'Auto-submitting...'
                        : hasUnsavedAnswers ? `Saving ${unsavedCount} answer(s)...`
                        : saving ? 'Submitting...'
                        : 'Review your answers before submitting'
                    }
                  >
                    <FiCheckCircle size={16} />
                    {hasUnsavedAnswers
                      ? (unsavedCount > 0
                          ? `Saving ${unsavedCount}...`
                          : 'Saving...')
                      : 'Review Answers'}
                  </button>
                ) : (
                  <button
                    className={`${styles.navButton} ${styles.primary}`}
                    onClick={handleNextWithAnswerConfirmation}
                    disabled={currentQuestionIndex === quiz.questions.length - 1}
                  >
                    Next
                    <FiArrowRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default QuizPlayer;
